

import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import { type DataView, type User, type NewUserHistory, type UserHistoryData, type UserFlattened, type ModelSettings, type Comment, type Workspace, type Agent, type AgentSystemUser } from '@/server/db/schema';
import { type FieldDefinition, type Filter, type SchemaFieldInput, type AuthUser, type AuthUserCreateRequest, type AuthUserUpdateRequest } from '@/types';
import { type PromptKind } from '@/lib/promptKinds';
import { type ToolSelection } from '@/agentToolRegistry/types';
import { type StripePriceDetail } from '@/types/billing';
import { type WorkspaceUserEnriched } from '@/types/workspace';
import { type SimulatedTypist } from '@/lib/broadcastSimulatedTyping';
import { canOnAllViews, type Access } from '@/lib/rbac';
import { flattenUser, generateJsonSchema, snakeToCamelKeys, generateSlug } from '@/lib/utils';
import { getFieldDefinitions } from '@/lib/dataFieldSchema';
import { createClient } from '@/lib/supabaseClient';
import throttle from 'lodash/throttle';
import type { ActionConfig, ManifestFieldDefinition, PageConfig } from '@/types/agentVersion';
import { DATA_CHANGED_DOM_EVENT } from '@/lib/pageBridge/protocol';
import { getQueryClient } from '@/components/QueryClientProvider';
import { EVAL_RUNS_ROOT } from '@/lib/evalRunsQuery';
import { resolveIcon } from '@/lib/iconMapping';
import { resolveRenderFunction } from '@/lib/renderFunctions';
import { type FocusViewParams, encodeFocusViewUrl } from '@/lib/viewClientUtils';
import { defaultDiscussionLink } from '@/lib/discussionFocus';
import { getUserColor, getInitials } from '@/lib/userColor';
import { commentsKeys, fetchComments } from '@/lib/commentsQuery';
import { showTakeoverResponseAlert, type NotificationPrefs, type NotificationSound } from '@/lib/takeoverNotifications';
import { type ChatChannel } from '@/lib/chatEvents';
import { type AssistantChatRef } from '@/lib/discussionAssistant';
import { mergeHistoryPage } from '@/lib/mergeHistoryPage';
import { useVersionTree } from '@/components/versions/useVersionTree';
import { promptPath, promptTypeOfPath } from '@/lib/promptFiles';

const log = console;

// Track userIds with in-flight takeover updates — avoid realtime overwriting optimistic state
const pendingUserUpdateIds = new Set<number>();

// Monotonic token for action fetches. Numbering responses lets the newest fetch win regardless
// of network order, since saving an agent triggers a refetch that can race the initial page load.
let actionsFetchSeq = 0;

// Pending optimistic cell edits — suppressed in realtime patches and re-overlaid on refetches
// to prevent stale pre-write snapshots from clobbering the optimistic value.
const pendingOptimisticEdits = new Map<number, Map<string, any>>();
export function trackOptimisticEdit(userId: number, fieldKey: string, value: any) {
	let m = pendingOptimisticEdits.get(userId);
	if (!m) {
		m = new Map();
		pendingOptimisticEdits.set(userId, m);
	}
	m.set(fieldKey, value);
}
export function clearOptimisticEdit(userId: number, fieldKey: string) {
	const m = pendingOptimisticEdits.get(userId);
	if (!m) return;
	m.delete(fieldKey);
	if (m.size === 0) pendingOptimisticEdits.delete(userId);
}
export function getPendingOptimisticEdits(): Map<number, Map<string, any>> {
	return pendingOptimisticEdits;
}

export interface PresenceState {
	systemUserId: string;
	email: string;
	color: string;
	initials: string;
	selectedUserId: number | null;
	lastActiveAt: string;
	// Populated for OAuth users (e.g. Google) — see CurrentUser.
	name?: string;
	avatarUrl?: string;
}

export interface ActiveCellEdit {
	editedBy: PresenceState;
	fieldKey: string;
	userId: number;
	timestamp: number;
}

// Default panel sizes in pixels
export const DEFAULT_LEFT_SIDEBAR_WIDTH = 240;
export const DEFAULT_RIGHT_SIDEBAR_WIDTH = 400;
export const DEFAULT_USER_PANEL_WIDTH = 480;
export const DEFAULT_CODE_PANEL_HEIGHT = 480;

/** The detail-fetch cache entry for one user, shared by every reader of that user's record. */
export const userQueryKey = (workspaceSlug: string, agentSlug: string, userId: number) =>
  ['/api', workspaceSlug, agentSlug, 'users', userId] as const;

/** The user's type from data already on the client: the open panels, the single-user cache, or a loaded users list page. */
function cachedUserType(get: () => AppState, userId: number): string | undefined {
  const { selectedUser, discussionUser, currentWorkspace, currentAgent } = get();
  if (selectedUser?.id === userId) return selectedUser.userType;
  if (discussionUser?.id === userId) return discussionUser.userType;
  const queryClient = getQueryClient();
  if (currentWorkspace && currentAgent) {
    const single = queryClient.getQueryData<{ userType?: string }>(userQueryKey(currentWorkspace.slug, currentAgent.slug, userId));
    if (single?.userType) return single.userType;
  }
  for (const [, data] of queryClient.getQueriesData<{ pages?: { users?: { id: number; userType?: string }[] }[] }>({ queryKey: ['users'] })) {
    for (const page of data?.pages ?? []) {
      const row = page.users?.find((u) => u.id === userId);
      if (row) return row.userType;
    }
  }
  return undefined;
}

export interface CurrentUser {
  id: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin';
  // Populated from Supabase user_metadata when the user signed in via an OAuth
  // provider (e.g. Google). Falls back to undefined for email/password users.
  name?: string;
  avatarUrl?: string;
}

export type UserRole = CurrentUser['role'];

// Extended types for agent members (with email from auth.users)
export interface AgentSystemUserWithEmail extends AgentSystemUser {
  email: string;
}

interface LayoutState {
  userPanelWidth: number;
  discussionPanelWidth: number;
  rightSidebarWidth: number;
  userPanelCollapsed: boolean;
  rightSidebarCollapsed: boolean;
  codePanelHeight: number;
  codePanelCollapsed: boolean;
  leftSidebarWidth: number;
  leftSidebarCollapsed: boolean;
  mainPanelVisible: boolean;
  apprenticeMode: 'sidebar' | 'setup';
  assistantFullScreen: boolean;
}

interface EvaluationResult {
  pass: boolean;
  reason: string;
  confidence: number;
  regeneratedResponse?: string;
}

interface ThumbsFeedbackContext {
  type: 'thumbs_up' | 'thumbs_down';
  messageId: number;
  userId: number;
  originalUserId: number;
  messageContent: string;
  conversationHistory: NewUserHistory[];
}

export interface PromptTypeMetadata {
  type: string;
  description: string | null;
  icon: string | null;
  latestVersion: number;
  totalVersions: number;
  hasActiveVersion: boolean;
  promptVersionColumnName?: string;
  promptVersionColumnTitle?: string;
  model?: string | null;
  modelSettings?: ModelSettings | null;
  meta?: {
    promptVersionColumnName?: string;
    superadminOnly?: boolean;
    tools?: ToolSelection;
    defaultMaxSteps?: number;
    [key: string]: any;
  };
  kind?: string;
  // Sidebar display order (from meta.sortOrder, default 0). Lower sorts first.
  sortOrder?: number;
}


// Sets to track pending requests (kept outside the state interface)
const pendingUserRequests = new Set<number>();
const historyReloads = new Map<number, { rerun: boolean; done: Promise<void> }>();
const pendingCommentRequests = new Set<number>();

/** Prompt-type routes answer `agentVersionId` when they wrote into the version tree instead of prompt rows. */
async function reloadWrittenVersion(body: { agentVersionId?: unknown } | null) {
	if (typeof body?.agentVersionId !== 'number') return;
	try {
		await useVersionTree.getState().reloadWritten(body.agentVersionId);
	} catch (error) {
		log.error('failed to reload agent version after write', { errorObj: error });
	}
}

// Throttle for realtime-triggered history reloads (leading + trailing edge, 1s per userId)
const HISTORY_THROTTLE_MS = 1000;
const historyThrottleTimers = new Map<number, ReturnType<typeof setTimeout>>();
const historyLastReloadTime = new Map<number, number>();
function throttledReloadHistory(userId: number, reloadFn: () => void) {
	const now = Date.now();
	const last = historyLastReloadTime.get(userId) ?? 0;
	const elapsed = now - last;

	if (elapsed >= HISTORY_THROTTLE_MS) {
		// Leading edge: fire immediately
		historyLastReloadTime.set(userId, now);
		reloadFn();
	} else {
		// Within window: schedule/replace trailing call
		const existing = historyThrottleTimers.get(userId);
		if (existing) clearTimeout(existing);
		const timer = setTimeout(() => {
			historyThrottleTimers.delete(userId);
			historyLastReloadTime.set(userId, Date.now());
			reloadFn();
		}, HISTORY_THROTTLE_MS - elapsed);
		historyThrottleTimers.set(userId, timer);
	}
}

// Snapshot of view state after loading (used for change detection)
let viewStateSnapshot: {
  currentViewId: string | null;
  filters: Filter[];
  visibleColumns: string[];
  columnOrder: string[];
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  logicalOperator: 'and' | 'or' | null;
} | null = null;


export interface AppState {
  // Data
  views: DataView[];
  /** False until reloadViews succeeds once — `views: []` before that means "unknown", not "none". */
  viewsLoaded: boolean;
  currentViewId: string | null;
  viewHasUnsavedChanges: boolean;
  isFocusView: boolean;
  focusViewTitle: string | null;
  focusViewSubtitle: string | null;
  /** Where "Exit focus" returns to, keyed by the focus view's title; in memory only, so a reload falls back to the default view. */
  focusReturn: { key: string; url: string } | null;
  fieldDefinitions: Record<string, FieldDefinition>;
  isAddingFieldDefinition: boolean;
  fieldDefinitionError: string | null;
  isRemovingFieldDefinition: boolean;
  removeFieldDefinitionError: string | null;
  userHistory: Record<number, NewUserHistory[]>;
  historyHasMore: Record<number, boolean>;
  historyLoadingMore: Record<number, boolean>;
  selectedUserId: number | null;
  selectedUser: UserFlattened | null;
  selectedUserHistory: NewUserHistory[] | null; // helper to hold user history for selected user
  thumbsFeedbackContext: ThumbsFeedbackContext | null;
  discussionUserId: number | null;
  discussionUser: User | null;

  // Takeover: track which users have active takeovers for browser alerts
  takeoverUsers: Record<number, { recipientId: string; label: string }>;

  currentWorkspace: Workspace | null;
  availableWorkspaces: Workspace[];
  currentAgent: Agent | null;
  availableAgents: Agent[];
  // Deliberately not persisted, so it starts false on every page load. Lets the
  // switcher tell "not resolved yet" apart from "this workspace has no agents".
  isWorkspaceContextLoaded: boolean;

  agentVersionManifestId: string | null;
  agentVersionPages: PageConfig[] | null;

  currentWorkspaceRole: 'workspace_superadmin' | 'workspace_owner' | 'workspace_member' | null;
  /** Workspace permissions (roles held for all agents, everything for the owner), for the workspace helpers in `permissions.ts`. */
  currentWorkspaceAccess: Access | null;
  currentAgentRole: 'agent_superadmin' | 'agent_admin' | 'agent_member' | null;
  /** The current person's grants on this agent; read them through `useAgentAccess`. */
  currentAgentAccess: Access | null;

  agentSystemUsers: AgentSystemUserWithEmail[];

  // Comments - only for discussion users, not selected users
  userComments: Record<number, Comment[]>;  // Maps userId -> array of comments
  discussionUserComments: Comment[] | null; // Helper for discussion user's comments
  isLoadingComments: boolean;

  filters: Filter[];
  visibleColumns: string[];
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  page: number;
  logicalOperator: 'and' | 'or' | null;
  testUserStatus?: string;
  pendingUserSimulations: number;
  /**
   * A message to hand to the sidebar assistant the next time it renders, set by something
   * elsewhere in the tree that wants to start a conversation it does not own — the prompt rail's
   * "Refine Prompt with AI" callout, for one. The sender sets it and opens the panel, and the
   * panel consumes it and clears it.
   */
  pendingAssistantMessage: string | null;
  /** Start a new chat before sending `pendingAssistantMessage`, so it runs isolated from the one on screen. */
  pendingAssistantNewChat: boolean;

  /** Text to put in the sidebar's input, focused but not sent. Consume-and-clear like `pendingAssistantMessage`. */
  pendingAssistantDraft: string | null;
  /** Start a new chat before placing `pendingAssistantDraft`, so the request is not mixed into the chat on screen. */
  pendingAssistantDraftNewChat: boolean;
  /** A short hint shown above the input with the draft, until the person clicks or types there. */
  pendingAssistantDraftHint: string | null;

  /** A sidebar chat to open, scrolled to one turn's reply. Consume-and-clear like `pendingAssistantMessage`. */
  pendingAssistantChat: AssistantChatRef | null;

  /** Bumped by `startSetup` so the sidebar kicks off exactly one interview per run. Not persisted. */
  setupRunId: number;


  promptTypes: PromptTypeMetadata[];
  selectedPromptType: string | null;
  /** The file open in the Code panel; `manifest.json` shows the manifest view. */
  selectedCodeFile: string | null;
  isLoadingPromptTypes: boolean;


  /**
   * @deprecated Legacy auth users management (superadmin access control)
   * Use workspaceMembers for workspace-scoped user management instead.
   * This will be removed after UI migration is complete.
   */
  authUsers: AuthUser[];
  /**
   * @deprecated Legacy auth users count
   * Use workspaceMembers.length for workspace-scoped user count instead.
   * This will be removed after UI migration is complete.
   */
  totalAuthUsersCount: number;
  /**
   * @deprecated Legacy selected auth user ID
   * Use workspaceMembers filtering instead.
   * This will be removed after UI migration is complete.
   */
  selectedAuthUserId: string | null;
  /**
   * @deprecated Legacy selected auth user
   * Use workspaceMembers filtering instead.
   * This will be removed after UI migration is complete.
   */
  selectedAuthUser: AuthUser | null;
  currentUser: CurrentUser | null;
  hasAdminAccess: boolean;

  workspaceMembers: WorkspaceUserEnriched[];
  isLoadingWorkspaceMembers: boolean;
  workspaceMembersError: string | null;

  stripePrices: StripePriceDetail[];
  isLoadingStripePrices: boolean;

  isEvaluating: boolean;
  evaluationResult: EvaluationResult | null;

  actions: ActionConfig[];
  bulkActions: ActionConfig[];
  singleActions: ActionConfig[];
  viewEntryActions: ActionConfig[];
  isLoadingActions: boolean;

  // View Action Running State (tracks locally-triggered runs for immediate UI feedback)
  runningActionDefinitionIds: Set<string>;
  setActionRunning: (definitionId: string, running: boolean) => void;

  // Background Refresh State (for SWR pattern)
  isRefreshingData: boolean;


  sidebarOpen: boolean;
  /**
   * Which settings section to open, or null when the dialog is closed. Lifted out of AppSidebar's
   * local state so any component can send the operator to a settings section — the prompt panel
   * links to Channels this way, because a channel binding is edited there rather than in the panel
   * (docs/impl/91).
   */
  openSettingsTab: string | null;
  layout: LayoutState;
  columnWidths: Record<string, number>;
  columnOrder: string[];
  isResizingColumn: boolean;

  notificationPrefs: NotificationPrefs;

  isLoadingViews: boolean;
  isLoadingAgentVersionPages: boolean;
  isLoadingFieldDefinitions: boolean;
  isLoadingUser: boolean;
  isLoadingAuthUsers: boolean;
  isLoadingAdminAccess: boolean;
  isCreatingAuthUser: boolean;
  isUpdatingAuthUser: string | null;
  isDeletingAuthUser: string | null;
  isResettingAuthUserPassword: string | null;

  setState: (state: Partial<AppState>) => void; // generic state setter
  captureViewSnapshot: () => void; // capture current view state for change detection
  focusOnFilters: (params: FocusViewParams) => void; // navigate to focus view with filters
  setViews: (views: DataView[]) => void;
  setSidebarOpen: (isOpen: boolean) => void;
  /** Open the settings dialog at a section, or pass null to close it. */
  setOpenSettingsTab: (tab: string | null) => void;
  createView: (data: {
    name: string;
    icon?: string;
    appState?: Partial<AppState>;
    permissions?: { role: 'all' | 'admin' | 'superadmin' }[] | null;
    kind?: 'grid' | 'canvas';
    html?: string;
    parentId?: number | null;
    visibility?: 'public' | 'private';
  }) => Promise<DataView>;
  updateView: (viewId: number, updates: Partial<DataView>) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
  toggleSidebar: () => void;
  setFieldDefinitions: (definitions: Record<string, FieldDefinition>) => void;
  addFieldDefinition: (input: SchemaFieldInput) => Promise<void>;
  removeFieldDefinition: (fieldName: string) => Promise<void>;
  setUserHistory: (userId: number, history: NewUserHistory[]) => void;
  getUserHistory: (userId: number) => NewUserHistory[];
  appendUserHistory: (userId: number, newHistoryItem: NewUserHistory) => void;
  updateUserHistory: (userId: number, historyId: number, data: UserHistoryData) => Promise<void>;
  /** Selecting a discussion opens only the discussion panel; `asReference` shows its conversation in the user panel instead. */
  setSelectedUserId: (id: number | null, options?: { asReference?: boolean }) => Promise<void>;
  setCurrentViewId: (viewId: string | null) => void;
  loadView: (viewConfig: Partial<AppState>) => void;
  setSelectedUser: (user: User | null) => void;
  setThumbsFeedbackContext: (context: ThumbsFeedbackContext | null) => void;
  setDiscussionUserId: (id: number | null) => void;
  updateUser: (updatedUser: Partial<User>) => Promise<void>;
  assignUser: (userId: number, assignedToId: string | null, assignedByEmail?: string) => Promise<void>;
  createTestUser: (options?: { cloneFromUserId?: number; initialUserId?: number; testDiscussionUserId?: number; userType?: 'sandbox' | 'discussion' | 'eval' | 'rated'; testStatus?: string; testWhatWentWrong?: string; testExpectedResponse?: string; testSuccessCriteria?: string; testDiscussionTitle?: string; testEvalTitle?: string; stopAtMessageId?: number; translateTo?: 'en' | 'es'; reviewStatus?: 'good' | 'bad'; data?: Record<string, any>; skipCloneComment?: boolean; channelType?: ChatChannel }) => Promise<User & { channelType?: ChatChannel | null }>;
  incrementPendingSimulations: () => void;
  decrementPendingSimulations: () => void;
  markAsRead: (userId: number) => Promise<void>;

  // Comment Actions
  setUserComments: (userId: number, comments: Comment[]) => void;
  getUserComments: (userId: number) => Comment[];
  appendUserComment: (userId: number, newComment: Comment) => void;
  createUserComment: (linkedUserId: number, content: string) => Promise<void>;
  updateUserComment: (userId: number, commentId: number, updates: Partial<Comment>) => Promise<void>;
  deleteUserComment: (userId: number, commentId: number) => Promise<void>;
  reloadUserComments: (userId: number) => Promise<void>;

  // Evaluation Actions
  /** Runs the run-evaluation action on one user; resolves with the eval_run user it wrote, or an error message. */
  runEvaluation: (
    userId: number,
    options?: { referenceUserIds?: number[]; versionId?: number | null }
  ) => Promise<{ runUserId: number | null; error?: string }>;

  /**
   * @deprecated Legacy auth user management actions (superadmin access control)
   * Use workspace-scoped user management actions instead.
   * These will be removed after UI migration is complete.
   */
  checkAdminAccess: () => Promise<void>;
  /**
   * @deprecated Use loadWorkspaceMembers instead
   */
  reloadAuthUsers: () => Promise<void>;
  /**
   * @deprecated Legacy auth user creation
   */
  createAuthUser: (userData: AuthUserCreateRequest) => Promise<AuthUser>;
  /**
   * @deprecated Legacy auth user update
   */
  updateAuthUser: (id: string, userData: AuthUserUpdateRequest) => Promise<AuthUser>;
  /**
   * @deprecated Legacy auth user deletion
   */
  deleteAuthUser: (id: string) => Promise<boolean>;
  /**
   * @deprecated Legacy password reset
   */
  resetAuthUserPassword: (id: string) => Promise<string>;
  /**
   * @deprecated Legacy auth user selection
   */
  setSelectedAuthUserId: (id: string | null) => void;

  updateWorkspaceUserRole: (userId: string, role: 'workspace_owner' | 'workspace_member') => Promise<void>;

  loadWorkspaceMembers: (agentId?: number) => Promise<void>;
  clearWorkspaceMembers: () => void;
  addUserToAgent: (userId: string, agentId: number, role: 'agent_superadmin' | 'agent_admin' | 'agent_member') => Promise<void>;
  removeUserFromAgent: (userId: string, agentId: number) => Promise<void>;
  updateUserAgentRole: (userId: string, agentId: number, role: 'agent_superadmin' | 'agent_admin' | 'agent_member') => Promise<void>;

  reloadStripePrices: () => Promise<void>;

  fetchPromptTypes: () => Promise<void>;
  reorderPromptTypes: (orderedTypeKeys: string[]) => Promise<void>;
  /** Opens the Code panel in the bottom slot, on `file` when given; a prompt file also becomes `selectedPromptType`. */
  openCodePanel: (file?: string) => void;
  createPromptType: (data: {
    type: string;
    content: string;
    description?: string;
    icon?: string;
    model?: string;
    modelSettings?: ModelSettings;
    meta?: any;
    kind?: PromptKind;
    /**
     * Whether version 1 is published. Defaults to published server-side; the "New agent" dialog
     * passes false so a subagent starts as a draft the operator deploys once it is written.
     */
    isActive?: boolean;
  }) => Promise<{ agentVersionId: number | null }>;
  deletePromptType: (type: string) => Promise<void>;
  updateTypeMetadata: (type: string, metadata: { description?: string; icon?: string; model?: string; modelSettings?: ModelSettings; meta?: any; kind?: PromptKind }) => Promise<void>;

  setColumnWidth: (column: string, width: number) => void;
  setColumnOrder: (columns: string[]) => void;
  collapseUserPanel: () => void;
  collapseDiscussionPanel: () => void;
  expandUserPanel: () => void;
  collapseRightSidebar: () => void;
  expandRightSidebar: () => void;
  toggleRightSidebar: () => void;
  enterAssistantFullScreen: () => void;
  exitAssistantFullScreen: () => void;
  /** Opens the assistant full screen in setup mode. A second call while already in setup just reopens full screen. */
  startSetup: () => void;
  exitSetupMode: () => void;
  refreshCurrentAgent: () => Promise<void>;
  collapseCodePanel: () => void;
  setPendingAssistantMessage: (message: string, opts?: { newChat?: boolean }) => void;
  clearPendingAssistantMessage: () => void;
  setPendingAssistantDraft: (draft: string, opts?: { newChat?: boolean; hint?: string }) => void;
  clearPendingAssistantDraft: () => void;
  /** Expands the right sidebar and asks it to open `chat` at its turn. */
  openAssistantChat: (chat: AssistantChatRef) => void;
  clearPendingAssistantChat: () => void;
  expandLeftSidebar: () => void;
  collapseLeftSidebar: () => void;
  setLeftSidebarWidth: (width: number) => void;
  setRightSidebarWidth: (width: number) => void;
  setUserPanelWidth: (width: number) => void;
  setDiscussionPanelWidth: (width: number) => void;
  setCodePanelHeight: (height: number) => void;
  toggleLeftSidebar: () => void;
  toggleMainPanel: () => void;

  reloadViews: () => Promise<void>;
  reloadFieldDefinitions: () => Promise<void>;
  invalidateUsersCache: () => void; // Helper to invalidate TQ users cache from Zustand
  invalidateViewsCache: () => void; // Helper to invalidate TQ views cache from Zustand
  reloadUser: (userId: number) => Promise<User | null>;
  reloadUserHistory: (userId: number) => Promise<void>;
  loadMoreHistory: (userId: number) => Promise<void>;
  loadTakeoverUsers: () => Promise<void>;

  setCurrentWorkspace: (workspace: Workspace | null) => void;
  setAvailableWorkspaces: (workspaces: Workspace[]) => void;
  setCurrentAgent: (agent: Agent | null) => void;
  setAvailableAgents: (agents: Agent[]) => void;
  setCurrentWorkspaceRole: (role: 'workspace_superadmin' | 'workspace_owner' | 'workspace_member' | null) => void;
  setCurrentAgentRole: (role: 'agent_superadmin' | 'agent_admin' | 'agent_member' | null) => void;
  setWorkspaceContext: (context: {
    workspace: Workspace;
    workspaces: Workspace[];
    workspaceRole: 'workspace_superadmin' | 'workspace_owner' | 'workspace_member' | null;
    workspaceAccess?: Access | null;
    agent: Agent;
    agents: Agent[];
    agentRole: 'agent_superadmin' | 'agent_admin' | 'agent_member' | null;
    agentAccess?: Access | null;
  }) => void;
  setAgentVersionPages: (pages: AppState['agentVersionPages']) => void;
  /** `fresh` bypasses the query cache: use it after something changed the version's pages. */
  loadAgentVersion: (options?: { fresh?: boolean }) => Promise<void>;
  switchWorkspace: (workspaceSlug: string) => Promise<void>;
  switchAgent: (agentSlug: string) => Promise<void>;
  loadWorkspaceData: () => Promise<void>;
  loadAgentData: () => Promise<void>;
  inviteWorkspaceMember: (email: string, role: 'workspace_owner' | 'workspace_member', agentAssignments?: Array<{ agentId: number; role: 'agent_superadmin' | 'agent_admin' | 'agent_member' }>) => Promise<void>;
  updateWorkspaceMemberRole: (memberId: string, role: 'workspace_owner' | 'workspace_member') => Promise<void>;
  removeWorkspaceMember: (memberId: string) => Promise<void>;

  // `force` bypasses the duplicate-request guard — for refreshing after a write that changes
  // which actions exist (saving or deploying an agent version, renaming a prompt type).
  fetchActions: (trigger?: 'bulk' | 'single' | 'viewEntry', options?: { force?: boolean }) => Promise<void>;
  getActionsByTrigger: (trigger: 'bulk' | 'single' | 'viewEntry') => ActionConfig[];

  onlineUsers: Record<string, PresenceState>;
  usersByRow: Record<number, PresenceState[]>;
  activeCellEdits: Record<string, ActiveCellEdit>;
  chatTypingUsers: Record<number, PresenceState[]>;
  simulatedTyping: Record<number, SimulatedTypist>;

  setOnlineUsers: (users: Record<string, PresenceState>) => void;
  setUsersByRow: (mapping: Record<number, PresenceState[]>) => void;
  setActiveCellEdit: (key: string, edit: ActiveCellEdit | null) => void;
  setChatTypingUser: (userId: number, user: PresenceState | null) => void;
  setSimulatedTyping: (userId: number, typist: SimulatedTypist | null) => void;
  clearAllCollabState: () => void;
}

export const useAppState = create<AppState>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          views: [],
          viewsLoaded: false,
          currentViewId: null,
          viewHasUnsavedChanges: false,
          isFocusView: false,
          focusReturn: null,
          focusViewTitle: null,
          focusViewSubtitle: null,
          sidebarOpen: true,
          openSettingsTab: null,
          isLoadingViews: false,
          fieldDefinitions: {},
          isAddingFieldDefinition: false,
          fieldDefinitionError: null,
          isRemovingFieldDefinition: false,
          removeFieldDefinitionError: null,
          isLoadingFieldDefinitions: false,
          isLoadingUser: false,
          userHistory: {},
          historyHasMore: {},
          historyLoadingMore: {},
          selectedUserId: null,
          selectedUser: null,
          selectedUserHistory: null,
          thumbsFeedbackContext: null,
          discussionUserId: null,
          discussionUser: null,
          takeoverUsers: {},
          currentWorkspace: null,
          availableWorkspaces: [],
          currentAgent: null,
          availableAgents: [],
          isWorkspaceContextLoaded: false,
          currentWorkspaceRole: null,
          currentWorkspaceAccess: null,
          currentAgentRole: null,
          currentAgentAccess: null,
          agentVersionPages: null,
          agentVersionManifestId: null,
          isLoadingAgentVersionPages: false,
          agentSystemUsers: [],
          userComments: {},
          discussionUserComments: null,
          isLoadingComments: false,
          authUsers: [],
          totalAuthUsersCount: 0,
          selectedAuthUserId: null,
          selectedAuthUser: null,
          currentUser: null,
          hasAdminAccess: false,
          workspaceMembers: [],
          isLoadingWorkspaceMembers: false,
          workspaceMembersError: null,
          filters: [],
          visibleColumns: ['id', 'name', 'email', 'last_response', 'status'],
          sortBy: 'lastResponseAt',
          sortOrder: 'desc',
          page: 1,
          logicalOperator: 'and',
          columnWidths: {},
          columnOrder: [],
          isResizingColumn: false,
          notificationPrefs: { sound: 'drop' as NotificationSound, browserNotifications: true },
          testUserStatus: undefined,
          pendingUserSimulations: 0,
          pendingAssistantMessage: null,
          pendingAssistantNewChat: false,
          pendingAssistantDraft: null,
          pendingAssistantDraftNewChat: false,
          pendingAssistantDraftHint: null,
          pendingAssistantChat: null,
          setupRunId: 0,
          isEvaluating: false,
          evaluationResult: null,
          promptTypes: [],
          selectedPromptType: null,
          selectedCodeFile: null,
          isLoadingPromptTypes: false,
          actions: [],
          bulkActions: [],
          singleActions: [],
          viewEntryActions: [],
          isLoadingActions: false,
          runningActionDefinitionIds: new Set<string>(),
          isRefreshingData: false,
          isLoadingAuthUsers: false,
          isLoadingAdminAccess: false,
          isCreatingAuthUser: false,
          isUpdatingAuthUser: null,
          isDeletingAuthUser: null,
          isResettingAuthUserPassword: null,
          stripePrices: [],
          isLoadingStripePrices: false,

          onlineUsers: {},
          usersByRow: {},
          activeCellEdits: {},
          chatTypingUsers: {},
          simulatedTyping: {},

          layout: {
            userPanelWidth: DEFAULT_USER_PANEL_WIDTH,
            discussionPanelWidth: DEFAULT_USER_PANEL_WIDTH,
            rightSidebarWidth: DEFAULT_RIGHT_SIDEBAR_WIDTH,
            userPanelCollapsed: true,
            rightSidebarCollapsed: true,
            codePanelHeight: DEFAULT_CODE_PANEL_HEIGHT,
            codePanelCollapsed: true,
            leftSidebarWidth: DEFAULT_LEFT_SIDEBAR_WIDTH,
            leftSidebarCollapsed: false,
            mainPanelVisible: true,
            apprenticeMode: 'sidebar' as const,
            assistantFullScreen: false,
          },

          setState: (state: Partial<AppState>) => {
            set(state);
          },

          // params.navigate (e.g. router.push) uses Next.js navigation so the server component +
          // ViewPageClient handle state, preserving back/forward support; without it we fall back
          // to pushState with an optimistic update, which doesn't support back navigation.
          focusOnFilters: (params: FocusViewParams) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              log.warn('focusOnFilters called without workspace/agent context');
              return;
            }

            const url = encodeFocusViewUrl(params);
            const fullUrl = `/${currentWorkspace.slug}/${currentAgent.slug}${url}`;

            if (params.navigate) {
              log.info('focusOnFilters navigating via Next.js router', {
                filterCount: params.filters?.length || 0,
                url: fullUrl,
              });
              params.navigate(fullUrl);
            } else {
              const stateUpdate: Partial<AppState> = {
                currentViewId: 'search',
                page: 1,
                isFocusView: true,
                focusViewTitle: params.title || null,
                focusViewSubtitle: params.subtitle || null,
              };

              if (params.filters) {
                stateUpdate.filters = params.filters;
              }
              if (params.columns) {
                stateUpdate.visibleColumns = params.columns;
              }
              if (params.sortBy) {
                stateUpdate.sortBy = params.sortBy;
              }
              if (params.sortOrder) {
                stateUpdate.sortOrder = params.sortOrder;
              }
              if (params.logicalOperator) {
                stateUpdate.logicalOperator = params.logicalOperator;
              }

              set(stateUpdate);

              if (typeof window !== 'undefined') {
                window.history.pushState({}, '', fullUrl);
              }

              get().invalidateUsersCache();

              if (params.selectedUserId) {
                get().setSelectedUserId(params.selectedUserId);
              }

              log.info('focusOnFilters applied via pushState', {
                filterCount: params.filters?.length || 0,
                columns: params.columns,
                logicalOperator: params.logicalOperator,
              });
            }
          },

          captureViewSnapshot: () => {
            const state = get();
            viewStateSnapshot = {
              currentViewId: state.currentViewId,
              filters: state.filters,
              visibleColumns: state.visibleColumns,
              columnOrder: state.columnOrder,
              sortBy: state.sortBy,
              sortOrder: state.sortOrder,
              logicalOperator: state.logicalOperator
            };
            // Reset the unsaved changes flag since we're capturing the current state as the baseline
            set({ viewHasUnsavedChanges: false });
            log.info('view snapshot captured', {
              currentViewId: state.currentViewId,
              snapshot: viewStateSnapshot
            });
          },


          incrementPendingSimulations: () => {
            set((state) => ({ pendingUserSimulations: state.pendingUserSimulations + 1 }));
          },

          decrementPendingSimulations: () => {
            set((state) => ({ pendingUserSimulations: Math.max(0, state.pendingUserSimulations - 1) }));
          },

          markAsRead: async (userId: number) => {
            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${userId}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  reviewStatus: 'read'
                })
              });

              if (!response.ok) {
                throw new Error('Failed to update user review status');
              }

              get().invalidateUsersCache();
              log.info('conversation marked as read', { userId });
            } catch (error) {
              log.error('failed to mark conversation as read', { errorObj: error, userId });
              throw error;
            }
          },

          createView: async (data: {
            name: string;
            icon?: string;
            appState?: Partial<AppState>;
            permissions?: { role: 'all' | 'admin' | 'superadmin' }[] | null;
            kind?: 'grid' | 'canvas';
            html?: string;
            parentId?: number | null;
            visibility?: 'public' | 'private';
          }) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }

            const slug = generateSlug(data.name.trim());

            // Convert permissions to the format expected by the API
            // API expects: null for all users, or { allowGroups: ['admin' | 'superadmin'] } for restricted access
            let permissions: { allowGroups: Array<'admin' | 'superadmin'> } | null = null;
            if (data.permissions && data.permissions.length > 0) {
              const groups = data.permissions
                .filter(p => p.role !== 'all')
                .map(p => p.role === 'admin' ? 'admin' : p.role === 'superadmin' ? 'superadmin' : null)
                .filter((g): g is 'admin' | 'superadmin' => g !== null);

              if (groups.length > 0) {
                permissions = { allowGroups: groups };
              }
            }

            try {
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(
                  data.kind === 'canvas'
                    ? {
                        kind: 'canvas',
                        name: data.name.trim(),
                        icon: data.icon,
                        permissions,
                        html: data.html,
                        parentId: data.parentId,
                        visibility: data.visibility,
                      }
                    : {
                        name: data.name.trim(),
                        searchParams: slug,
                        icon: data.icon || 'users',
                        appState: data.appState || {},
                        permissions,
                        parentId: data.parentId,
                        visibility: data.visibility,
                      },
                ),
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to create view' }));
                throw new Error(errorData.error || 'Failed to create view');
              }

              const savedView = await response.json();

              // Invalidate TanStack Query cache immediately so sidebar updates
              get().invalidateViewsCache();

              await get().reloadViews();

              log.info('view created successfully', { viewId: savedView.id, name: savedView.name });
              return savedView;
            } catch (error) {
              log.error('failed to create view', { errorObj: error });
              throw error;
            }
          },

          updateView: async (viewId: number, updates: Partial<DataView>) => {
            log.info('updating view', { viewId, updates });

            const previousState = get();
            const previousView = previousState.views.find(v => v.id === viewId);

            if (!previousView) {
              log.error('view not found in state', { viewId });
              return;
            }

            set((state) => ({
              views: state.views.map(view =>
                view.id === viewId
                  ? { ...view, ...updates, updatedAt: new Date() }
                  : view
              )
            }));

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views/${viewId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates),
              });

              if (!response.ok) {
                throw new Error('Failed to update view');
              }

              const updatedView = await response.json();

              set((state) => ({
                viewHasUnsavedChanges: false,
                views: state.views.map(view =>
                  view.id === viewId ? updatedView : view
                )
              }));

              get().invalidateViewsCache();

              log.info('view updated successfully', { viewId });
            } catch (error) {
              set({
                views: previousState.views
              });

              log.error('failed to update view', { errorObj: error });
              throw error;
            }
          },

          deleteView: async (viewId: string) => {
            log.info('deleting view', { viewId });

            const previousState = get();

            set((state) => {
              const updatedViews = state.views.filter(view => view.id.toString() !== viewId);

              // If the deleted view was currently selected, switch to "All Activity"
              if (state.currentViewId === viewId) {
                return {
                  views: updatedViews,
                  currentViewId: '0',
                  filters: [{ field: "testUser", operator: "eq", value: false }],
                  visibleColumns: ['lastResponseAt', 'reviewStatus', 'recipientId']
                };
              }

              return { views: updatedViews };
            });

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views/${viewId}`, {
                method: 'DELETE',
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to delete view');
              }

              get().invalidateViewsCache();

              log.info('view deleted successfully', { viewId });
            } catch (error) {
              set({
                views: previousState.views,
                currentViewId: previousState.currentViewId,
                filters: previousState.filters,
                visibleColumns: previousState.visibleColumns
              });

              log.error('failed to delete view', { errorObj: error });
              throw error;
            }
          },


          setViews: (views) => set({ views }),
          setSidebarOpen: (isOpen) => set({ sidebarOpen: isOpen }),
          setOpenSettingsTab: (tab) => set({ openSettingsTab: tab }),
          toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
          setUserHistory: (userId, history) => {
            set((state) => {
              const newState = {
                userHistory: {
                  ...state.userHistory,
                  [userId]: history
                },
              };
              return newState;
            });
          },
          getUserHistory: (userId: number) => {
            return get().userHistory[userId] || [];
          },
          appendUserHistory: (userId, newHistoryItem) => {
            const currentHistory = get().userHistory[userId] || [];
            set((state) => {
              const newState = {
                userHistory: {
                  ...state.userHistory,
                  [userId]: [...currentHistory, newHistoryItem]
                },
              };
              return newState;
            });
          },
          setUserComments: (userId, comments) => {
            set((state) => {
              const newState = {
                userComments: {
                  ...state.userComments,
                  [userId]: comments
                },
              };
              return newState;
            });
          },
          getUserComments: (userId: number) => {
            return get().userComments[userId] || [];
          },
          appendUserComment: (userId, newComment) => {
            const currentComments = get().userComments[userId] || [];
            set((state) => {
              const newState = {
                userComments: {
                  ...state.userComments,
                  [userId]: [...currentComments, newComment]
                },
              };
              return newState;
            });
          },
          updateUserComment: async (userId: number, commentId: number, updates: Partial<Comment>) => {
            const state = get();
            const currentComments = state.userComments[userId] || [];

            // Find the comment to update
            const commentIndex = currentComments.findIndex(comment => comment.id === commentId);
            if (commentIndex === -1) {
              log.error('comment not found', { userId, commentId });
              return;
            }

            // Store current state for potential revert
            const previousComment = currentComments[commentIndex];

            // Create a new comments array with the updated comment
            const updatedComments = [...currentComments];
            updatedComments[commentIndex] = {
              ...previousComment,
              ...updates,
            } as Comment;

            // Optimistically update client state. CommentThread renders from the query cache, so the
            // edit goes into both that and Zustand.
            const queryClient = getQueryClient();
            set((state) => ({
              userComments: {
                ...state.userComments,
                [userId]: updatedComments
              }
            }));
            queryClient.setQueryData(commentsKeys.byLinkedUserId(userId), updatedComments);

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/comments/${commentId}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates)
              });

              if (!response.ok) {
                throw new Error('Failed to update comment');
              }

              // If updated content contains user references (user#123), reload the discussion user
              // to get the updated meta.referencedUserIds field
              if (updates.content) {
                const hasUserReferences = /user#(\d+)/i.test(updates.content);
                if (hasUserReferences) {
                  const state = get();
                  if (state.discussionUserId === userId) {
                    await get().reloadUser(userId);
                    log.info('reloaded discussion user after comment edit with user references', { userId });
                  }
                }
              }

              log.info('comment updated successfully', { userId, commentId });
            } catch (error) {
              // Revert optimistic update on error, in both places it was applied
              set((state) => ({
                userComments: {
                  ...state.userComments,
                  [userId]: currentComments
                }
              }));
              queryClient.setQueryData(commentsKeys.byLinkedUserId(userId), currentComments);
              log.error('failed to update comment', { errorObj: error, userId, commentId });
              throw error;
            }
          },
          deleteUserComment: async (userId: number, commentId: number) => {
            const state = get();
            const currentComments = state.userComments[userId] || [];

            const commentIndex = currentComments.findIndex(comment => comment.id === commentId);
            if (commentIndex === -1) {
              log.error('comment not found', { userId, commentId });
              return;
            }

            // Store current state for potential revert
            const deletedComment = currentComments[commentIndex];

            const updatedComments = currentComments.filter(comment => comment.id !== commentId);

            set((state) => ({
              userComments: {
                ...state.userComments,
                [userId]: updatedComments
              }
            }));

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/comments/${commentId}`, {
                method: 'DELETE',
              });

              if (!response.ok) {
                throw new Error('Failed to delete comment');
              }

              log.info('comment deleted successfully', { userId, commentId });
            } catch (error) {
              set((state) => ({
                userComments: {
                  ...state.userComments,
                  [userId]: currentComments
                }
              }));
              log.error('failed to delete comment', { errorObj: error, userId, commentId });
              throw error;
            }
          },
          reloadUserComments: async (userId: number) => {
            if (pendingCommentRequests.has(userId)) {
              log.info('skipping duplicate comment request', { userId });
              return;
            }

            try {
              pendingCommentRequests.add(userId);

              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              // Fetching through TanStack Query updates an open CommentThread as well as the Zustand
              // copy. `staleTime: 0` overrides the global Infinity default, which would otherwise
              // return a cached list that predates the comment just written.
              const queryClient = getQueryClient();
              const comments = await queryClient.fetchQuery({
                queryKey: commentsKeys.byLinkedUserId(userId),
                queryFn: () => fetchComments(currentWorkspace.slug, currentAgent.slug, userId),
                staleTime: 0,
              });
              set((state) => ({
                userComments: {
                  ...state.userComments,
                  [userId]: comments
                }
              }));
            } catch (error) {
              log.error('failed to reload user comments', { errorObj: error, userId });
            } finally {
              pendingCommentRequests.delete(userId);
            }
          },
          createUserComment: async (linkedUserId: number, content: string) => {
            if (!linkedUserId || !content.trim()) {
              log.error('cannot create comment: missing linkedUserId or content');
              return;
            }

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  linkedUserId,
                  content: content.trim(),
                }),
              });

              if (!response.ok) throw new Error('Failed to create comment');

              await get().reloadUserComments(linkedUserId);

              // If comment contains user references (user#123), reload the discussion user
              // to get the updated meta.referencedUserIds field
              const hasUserReferences = /user#(\d+)/i.test(content);
              if (hasUserReferences) {
                const state = get();
                if (state.discussionUserId === linkedUserId) {
                  await get().reloadUser(linkedUserId);
                  log.info('reloaded discussion user after comment with user references', { linkedUserId });
                }
              }

              log.info('comment created successfully', { linkedUserId });
            } catch (error) {
              log.error('failed to create comment', { errorObj: error, linkedUserId });
              throw error;
            }
          },
          updateUserHistory: async (userId: number, historyId: number, data: UserHistoryData) => {
            const state = get();
            const currentHistory = state.userHistory[userId] || [];

            const historyItemIndex = currentHistory.findIndex(item => item.id === historyId);
            if (historyItemIndex === -1) {
              log.error('history item not found', { userId, historyId });
              return;
            }

            // Store current state for potential revert
            const previousHistoryItem = currentHistory[historyItemIndex];

            if (!previousHistoryItem?.event) {
              log.error('invalid history item', { userId, historyId });
              return;
            }

            const updatedHistory = [...currentHistory] as NewUserHistory[];
            const historyItemToUpdate = updatedHistory[historyItemIndex];
            const historyItemData = historyItemToUpdate?.data || {};
            const updatedData = {
              ...data,
              ...historyItemData
            };

            updatedHistory[historyItemIndex] = {
              ...historyItemToUpdate,
              agentId: previousHistoryItem.agentId,
              event: historyItemToUpdate?.event || previousHistoryItem.event, // Ensure event is always defined
              data: updatedData
            };

            set((state) => ({
              userHistory: {
                ...state.userHistory,
                [userId]: updatedHistory
              }
            }));

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace or agent context available');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/user-history/${historyId}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data })
              });

              if (!response.ok) {
                throw new Error('Failed to update user history');
              }

              log.info('user history updated successfully', { userId, historyId });
            } catch (error) {
              set((state) => ({
                userHistory: {
                  ...state.userHistory,
                  [userId]: currentHistory
                }
              }));
              log.error('failed to update user history', { errorObj: error, userId, historyId });
              throw error;
            }
          },
          setSelectedUserId: async (id, options) => {
            // See simplified-navigation-plan.md for the state/URL sync approach.
            // Opening a discussion leaves whatever conversation the user panel shows in place.
            const previousSelectedUserId = get().selectedUserId;
            const openDiscussion = (discussionId: number) => {
              set((state) => ({ ...state, selectedUserId: previousSelectedUserId, isLoadingUser: false }));
              get().setDiscussionUserId(discussionId);
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.set('discussionUserId', discussionId.toString());
                if (previousSelectedUserId) url.searchParams.set('selectedUserId', previousSelectedUserId.toString());
                else url.searchParams.delete('selectedUserId');
                window.history.replaceState({}, '', url.toString());
              }
            };

            // Deciding from already loaded data keeps the user panel from opening and closing again
            // when the fetch turns out to be a discussion.
            if (id && !options?.asReference && cachedUserType(get, id) === 'discussion') {
              openDiscussion(id);
              return;
            }

            set((state) => ({ ...state, selectedUserId: id }));

            if (typeof window !== 'undefined') {
              const url = new URL(window.location.href);
              if (id !== null && id !== undefined) {
                url.searchParams.set('selectedUserId', id.toString());
              } else {
                url.searchParams.delete('selectedUserId');
              }
              window.history.replaceState({}, '', url.toString());
            }

            if (id) {
              set((state) => ({ ...state, isLoadingUser: true }));
              try {
                const { currentWorkspace, currentAgent } = get();
                if (!currentWorkspace || !currentAgent) {
                  throw new Error('No workspace/agent context');
                }

                const queryClient = getQueryClient();

                const [dbUser] = await Promise.all([
                  queryClient.fetchQuery({
                    queryKey: userQueryKey(currentWorkspace.slug, currentAgent.slug, id),
                    queryFn: async () => {
                      const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${id}`);
                      if (!response.ok) throw new Error('Failed to fetch user');
                      return response.json();
                    },
                  }),
                  get().reloadUserHistory(id),
                ]);

                const user = flattenUser(dbUser);

                // The requested id may have been merged away, in which case the route serves the
                // survivor; move the selection there so every later fetch uses the live id.
                const resolvedId = typeof user?.id === 'number' ? user.id : id;
                if (resolvedId !== id) {
                  await get().reloadUserHistory(resolvedId);
                  set((state) => ({ ...state, selectedUserId: resolvedId }));
                  if (typeof window !== 'undefined') {
                    const resolvedUrl = new URL(window.location.href);
                    resolvedUrl.searchParams.set('selectedUserId', resolvedId.toString());
                    window.history.replaceState({}, '', resolvedUrl.toString());
                  }
                }

                if (user?.userType === 'discussion' && !options?.asReference) {
                  openDiscussion(user.id);
                  return;
                }

                if (user?.userType === 'discussion') {
                  get().setDiscussionUserId(user.id);
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.set('discussionUserId', user.id.toString());
                    window.history.replaceState({}, '', url.toString());
                  }
                }

                set((state) => ({
                  ...state,
                  selectedUser: user,
                  selectedUserHistory: state.userHistory[resolvedId],
                  isLoadingUser: false
                }));

                get().expandUserPanel();

                set((state) => ({ ...state, evaluationResult: null }));
              } catch (error) {
                log.error('failed to fetch user', { errorObj: error, userId: id });
                set((state) => ({ ...state, isLoadingUser: false }));
              }
            } else {
              set((state) => ({ ...state, selectedUser: null }));
              get().collapseUserPanel();
            }
          },
          setSelectedUser: (user) => set({ selectedUser: user }),
          setThumbsFeedbackContext: (context) => set({ thumbsFeedbackContext: context }),
          setCurrentViewId: (viewId) => set({ currentViewId: viewId }),
          loadView: (viewConfig: Partial<AppState>) => {
            set((state) => ({
              ...state,
              ...viewConfig,
              viewHasUnsavedChanges: false
            }));

            // TQ auto-refetches when query key (filters, sort, etc.) changes
            get().invalidateUsersCache();
          },
          setDiscussionUserId: (id: number | null) => {
            set({ discussionUserId: id });
            if (typeof window !== 'undefined') {
              const url = new URL(window.location.href);
              if (id) url.searchParams.set('discussionUserId', id.toString());
              else url.searchParams.delete('discussionUserId');
              if (url.href !== window.location.href) window.history.replaceState({}, '', url.toString());
            }
            if (id) {
              // Fetch the user data (TQ manages users list, so we always fetch here)
              get().reloadUser(id).then(user => {
                if (user) {
                  set({ discussionUser: user });
                }
              });

              get().reloadUserComments(id).then(() => {
                const state = get();
                set({ discussionUserComments: state.userComments[id] || [] });
              });
            } else {
              set({ discussionUser: null, discussionUserComments: null });
            }
          },

          collapseDiscussionPanel: () => {
            set({ discussionUserId: null, discussionUser: null, discussionUserComments: null });

            if (typeof window !== 'undefined') {
              const url = new URL(window.location.href);
              url.searchParams.delete('discussionUserId');
              window.history.replaceState({}, '', url.toString());
            }
          },

          setColumnWidth: (column: string, width: number) => {
            set((state) => ({
              columnWidths: {
                ...state.columnWidths,
                [column]: width
              }
            }));
          },
          setColumnOrder: (columns: string[]) => {
            const currentState = get();
            log.info('setting column order', {
              columns,
              currentViewId: currentState.currentViewId,
              previousColumnOrder: currentState.columnOrder
            });
            set({ columnOrder: columns }, false, 'setColumnOrder');
          },
          collapseUserPanel: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                userPanelCollapsed: true
              }
            })),
          expandUserPanel: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                userPanelCollapsed: false
              }
            })),
          collapseRightSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                rightSidebarCollapsed: true,
                assistantFullScreen: false,
              }
            })),
          expandRightSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                rightSidebarCollapsed: false
              }
            })),
          toggleRightSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                rightSidebarCollapsed: !state.layout.rightSidebarCollapsed
              }
            })),
          enterAssistantFullScreen: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                assistantFullScreen: true,
                rightSidebarCollapsed: false,
              }
            })),
          exitAssistantFullScreen: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                assistantFullScreen: false,
              }
            })),
          startSetup: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                apprenticeMode: 'setup' as const,
                assistantFullScreen: true,
                rightSidebarCollapsed: false,
              },
              setupRunId: state.layout.apprenticeMode === 'setup' ? state.setupRunId : state.setupRunId + 1,
            })),
          exitSetupMode: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                apprenticeMode: 'sidebar' as const,
                assistantFullScreen: false,
              }
            })),
          refreshCurrentAgent: async () => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) return;
            try {
              const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/agents/${currentAgent.slug}`);
              if (!response.ok) return;
              const data = await response.json();
              const agent: Agent | undefined = data?.agent;
              if (!agent || get().currentAgent?.id !== agent.id) return;
              set((state) => ({
                currentAgent: agent,
                availableAgents: state.availableAgents.map((a) => (a.id === agent.id ? agent : a)),
              }));
            } catch (error) {
              log.error('failed to refresh current agent', { errorObj: error });
            }
          },
          collapseCodePanel: () => {
            set((state) => ({ layout: { ...state.layout, codePanelCollapsed: true } }));
          },
          setPendingAssistantMessage: (message: string, opts?: { newChat?: boolean }) => {
            set({ pendingAssistantMessage: message, pendingAssistantNewChat: opts?.newChat === true });
          },
          clearPendingAssistantMessage: () => {
            set({ pendingAssistantMessage: null, pendingAssistantNewChat: false });
          },
          setPendingAssistantDraft: (draft: string, opts?: { newChat?: boolean; hint?: string }) => {
            set({ pendingAssistantDraft: draft, pendingAssistantDraftNewChat: opts?.newChat === true, pendingAssistantDraftHint: opts?.hint ?? null });
          },
          clearPendingAssistantDraft: () => {
            set({ pendingAssistantDraft: null, pendingAssistantDraftHint: null });
          },
          openAssistantChat: (chat: AssistantChatRef) => {
            get().expandRightSidebar();
            set({ pendingAssistantChat: chat });
          },
          clearPendingAssistantChat: () => {
            set({ pendingAssistantChat: null });
          },
          setLeftSidebarWidth: (width: number) =>
            set((state) => ({
              layout: {
                ...state.layout,
                leftSidebarWidth: width
              }
            })),
          setRightSidebarWidth: (width: number) =>
            set((state) => ({
              layout: {
                ...state.layout,
                rightSidebarWidth: width
              }
            })),
          setUserPanelWidth: (width: number) =>
            set((state) => ({
              layout: {
                ...state.layout,
                userPanelWidth: width
              }
            })),
          setDiscussionPanelWidth: (width: number) =>
            set((state) => ({
              layout: {
                ...state.layout,
                discussionPanelWidth: width
              }
            })),
          setCodePanelHeight: (height: number) =>
            set((state) => ({ layout: { ...state.layout, codePanelHeight: height } })),
          collapseLeftSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                leftSidebarCollapsed: true
              }
            })),
          expandLeftSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                leftSidebarCollapsed: false
              }
            })),
          toggleLeftSidebar: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                leftSidebarCollapsed: !state.layout.leftSidebarCollapsed
              }
            })),

          toggleMainPanel: () =>
            set((state) => ({
              layout: {
                ...state.layout,
                mainPanelVisible: !state.layout.mainPanelVisible
              }
            })),

          updateUser: async (updatedUser: Partial<User>) => {
            const userId = updatedUser.id as number;
            if (userId && (updatedUser.takeoverActive !== undefined || updatedUser.takeoverSuggesting !== undefined)) {
              pendingUserUpdateIds.add(userId);
            }
            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const queryClient = getQueryClient();
              const userQueryKey = ["/api", currentWorkspace.slug, currentAgent.slug, "users", updatedUser.id];

              // TQ optimistic pattern: cancel in-flight queries to prevent stale data overwriting
              await queryClient.cancelQueries({ queryKey: ['users'] });
              await queryClient.cancelQueries({ queryKey: userQueryKey });

              // Optimistic update: merge new fields into Zustand state immediately
              const prevState = get();
              const optimisticSelected = prevState.selectedUserId === updatedUser.id && prevState.selectedUser
                ? { ...prevState.selectedUser, ...updatedUser, data: { ...(prevState.selectedUser as any).data, ...updatedUser } } as User
                : prevState.selectedUser;
              const optimisticDiscussion = prevState.discussionUserId === updatedUser.id && prevState.discussionUser
                ? { ...prevState.discussionUser, ...updatedUser, data: { ...(prevState.discussionUser as any).data, ...updatedUser } } as User
                : prevState.discussionUser;
              set({ selectedUser: optimisticSelected, discussionUser: optimisticDiscussion });

              // Also set TQ cache optimistically so invalidation doesn't refetch stale data
              queryClient.setQueryData(userQueryKey, (old: any) =>
                old ? { ...old, ...updatedUser, data: { ...(old.data || {}), ...updatedUser } } : old
              );

              // Send flattened data directly - API handles the split via updateUserWithCustomData
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${updatedUser.id}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedUser),
              });

              if (!response.ok) {
                set({ selectedUser: prevState.selectedUser, discussionUser: prevState.discussionUser });
                throw new Error('Failed to update user');
              }

              const updated = await response.json();
              // Flatten the user data before storing in state (extracts data.* fields to top level)
              const flattenedUser = flattenUser(updated);

              set((state) => ({
                selectedUser: state.selectedUserId === flattenedUser.id ? flattenedUser : state.selectedUser,
                discussionUser: state.discussionUserId === flattenedUser.id ? flattenedUser : state.discussionUser
              }));

              console.log('[CACHE-DBG] updateUser INVALIDATE (will trigger refetch)', { t: Date.now(), userId: updatedUser.id });
              get().invalidateUsersCache();
            } catch (error) {
              log.error('Error updating user:', error);
              throw error;
            } finally {
              if (userId && (updatedUser.takeoverActive !== undefined || updatedUser.takeoverSuggesting !== undefined)) {
                pendingUserUpdateIds.delete(userId);
              }
            }
          },

          assignUser: async (userId: number, assignedToId: string | null, assignedByEmail?: string) => {
            try {
              const state = get();

              await get().updateUser({
                id: userId,
                assignedTo: assignedToId
              });

              // Send email notification (non-blocking)
              if (assignedToId !== null) {
                const { currentWorkspace, currentAgent } = get();
                if (currentWorkspace && currentAgent) {
                  fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/notifications/assignment`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      userId,
                      assignedToId,
                      assignedByEmail: assignedByEmail || state.currentUser?.email,
                    }),
                  }).catch((error) => {
                    log.error('Failed to send assignment notification:', { errorObj: error, userId, assignedToId });
                  });
                }
              }

              // Automatically add a comment to the discussion if one is open
              if (state.discussionUserId) {
                const currentUserEmail = assignedByEmail || state.currentUser?.email || 'System';
                const assignedToUser = state.workspaceMembers.find(u => u.systemUserId === assignedToId);

                let commentContent: string;
                if (assignedToId === null) {
                  commentContent = `${currentUserEmail} unassigned user#${userId}`;
                } else {
                  const assignedToEmail = assignedToUser?.email || assignedToId;
                  commentContent = `${currentUserEmail} assigned user#${userId} to ${assignedToEmail}`;
                }

                // Create the comment asynchronously without blocking the return
                get().createUserComment(state.discussionUserId, commentContent).catch((error) => {
                  log.error('failed to create automatic assignment comment', {
                    errorObj: error,
                    discussionUserId: state.discussionUserId,
                    userId,
                    assignedToId
                  });
                });
              }

              log.info('user assignment updated', { userId, assignedToId });
            } catch (error) {
              log.error('Error assigning user:', { errorObj: error, userId, assignedToId });
              throw error;
            }
          },

          createTestUser: async (options = {}) => {
            log.info('creating test user', options);
            const { currentWorkspace, currentAgent, selectedUser, discussionUserId: focusedId, discussionUser } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }
            const linkedDiscussionId =
              options.testDiscussionUserId ??
              defaultDiscussionLink({
                userType: options.userType,
                cloneFromUserId: options.cloneFromUserId,
                openUser: selectedUser && {
                  id: selectedUser.id,
                  userType: selectedUser.userType,
                  testDiscussionUserId: selectedUser.testDiscussionUserId,
                },
                focusedUser: discussionUser?.id === focusedId ? discussionUser : null,
              });
            try {
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/create-test-user`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  cloneFromUserId: options.cloneFromUserId,
                  initialUserId: options.initialUserId,
                  testDiscussionUserId: linkedDiscussionId,
                  userType: options.userType || 'sandbox',
                  testStatus: options.testStatus || 'sandbox',
                  testWhatWentWrong: options.testWhatWentWrong,
                  testExpectedResponse: options.testExpectedResponse,
                  testSuccessCriteria: options.testSuccessCriteria,
                  testDiscussionTitle: options.testDiscussionTitle,
                  testEvalTitle: options.testEvalTitle,
                  stopAtMessageId: options.stopAtMessageId,
                  translateTo: options.translateTo,
                  reviewStatus: options.reviewStatus,
                  data: options.data,
                  channelType: options.channelType
                }),
              });
              if (!response.ok) {
                const error = await response.text();
                log.error('failed to create test user', { error, options });
                throw new Error(`Failed to create test user: ${error}`);
              }
              const newTestUser = await response.json();

              // Invalidate TQ cache so the new user appears in the list
              get().invalidateUsersCache();

              // Reload field definitions in case new data fields were auto-added to schema
              get().reloadFieldDefinitions();

              // Automatically add a comment to the discussion if one is open. Callers creating a
              // discussion of their own pass skipCloneComment to avoid a stray notice on an
              // unrelated open discussion.
              const state = get();
              const discussionUserId = linkedDiscussionId || state.discussionUserId;
              if (discussionUserId && options.cloneFromUserId && !options.skipCloneComment) {
                const currentUserEmail = state.currentUser?.email || 'System';

                let userTypeLabel = 'user';
                if (newTestUser.userType === 'eval') {
                  userTypeLabel = 'an eval user';
                } else if (newTestUser.userType === 'discussion') {
                  userTypeLabel = 'a discussion user';
                } else if (newTestUser.userType === 'sandbox') {
                  userTypeLabel = 'a sandbox user';
                } else if (newTestUser.userType === 'live') {
                  userTypeLabel = 'a live user';
                } else if (newTestUser.userType === 'rated') {
                  userTypeLabel = 'a rated user';
                }

                const commentContent = `${currentUserEmail} cloned user#${options.cloneFromUserId} to user#${newTestUser.id} as ${userTypeLabel}`;

                // Create the comment asynchronously without blocking the return
                get().createUserComment(discussionUserId, commentContent).catch((error) => {
                  log.error('failed to create automatic clone notification comment', {
                    errorObj: error,
                    discussionUserId,
                    cloneFromUserId: options.cloneFromUserId,
                    newUserId: newTestUser.id
                  });
                });
              }

              return newTestUser;
            } catch (error) {
              log.error('Error creating test user:', error);
              throw error;
            }
          },

          setFieldDefinitions: (definitions) => set({ fieldDefinitions: definitions }),

          addFieldDefinition: async (input: SchemaFieldInput) => {
            set({ isAddingFieldDefinition: true, fieldDefinitionError: null });

            try {
              if (!input.title) {
                throw new Error('Field title is required');
              }

              if (input.type === 'select' && (!input.selectableValues || input.selectableValues.length === 0)) {
                throw new Error('At least one selectable value is required');
              }

              if (input.type === 'ai prompt' && (!input.aiModel || !input.aiPrompt)) {
                throw new Error('AI model and prompt are required');
              }

              const partialSchema = generateJsonSchema({
                columnName: input.title,
                selectedType: input.type,
                description: input.description,
                selectableValues: input.selectableValues || [],
                aiModel: input.aiModel,
                aiPrompt: input.aiPrompt,
                renderType: input.renderType
              });

              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/schema`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(partialSchema),
              });

              if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update schema');
              }

              await get().reloadFieldDefinitions();
              set({ isAddingFieldDefinition: false });
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to update schema';
              set({ fieldDefinitionError: errorMessage, isAddingFieldDefinition: false });
              throw error;
            }
          },

          reloadFieldDefinitions: async () => {
            const { currentWorkspace, currentAgent, workspaceMembers, isLoadingWorkspaceMembers } = get();
            if (!currentWorkspace || !currentAgent) return;

            if (isLoadingWorkspaceMembers) {
              return new Promise<void>((resolve) => {
                const unsubscribe = useAppState.subscribe(
                  (state) => state.isLoadingWorkspaceMembers,
                  (isLoading) => {
                    if (!isLoading) {
                      unsubscribe();
                      get().reloadFieldDefinitions().then(resolve);
                    }
                  }
                );
              });
            }

            if (workspaceMembers.length === 0 && !isLoadingWorkspaceMembers) {
              await get().loadWorkspaceMembers(currentAgent.id);
            }

            set({ isLoadingFieldDefinitions: true });
            try {
              const queryClient = getQueryClient();

              if (currentWorkspace && currentAgent) {
                queryClient.invalidateQueries({
                  queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "schema"]
                });
              }

              // Get base field definitions from schema (reads members from state internally)
              const definitions = await getFieldDefinitions(undefined);

              // Merge in agent version dataSchema fields (manifest-driven dynamic options)
              if (currentWorkspace && currentAgent) {
                try {
                  const versionData = await queryClient.fetchQuery({
                    queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "version"],
                    queryFn: async () => {
                      const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/version`);
                      if (!response.ok) throw new Error('Failed to fetch version');
                      return response.json();
                    },
                  });

                  const dataSchema = versionData?.dataSchema as Record<string, ManifestFieldDefinition> | undefined;
                  const versionId = versionData?.versionId as number | undefined;

                  if (dataSchema && versionId) {
                    for (const [fieldKey, manifestField] of Object.entries(dataSchema)) {
                      const IconComponent = manifestField.icon ? resolveIcon(manifestField.icon) : resolveIcon('FileText');
                      const fieldDef: FieldDefinition = {
                        key: fieldKey,
                        type: manifestField.type,
                        label: manifestField.label,
                        description: manifestField.description,
                        icon: <IconComponent className="mr-2 h-4 w-4" />,
                        searchable: manifestField.searchable ?? false,
                        displayable: manifestField.displayable ?? false,
                        placeholder: manifestField.placeholder,
                        options: [], // Will be populated below
                        internal: true, // Mark as internal (agent-managed, not user-editable)
                      };

                      if (manifestField.renderType) {
                        fieldDef.render = resolveRenderFunction(manifestField.renderType);
                      }

                      if (manifestField.options) {
                        if (Array.isArray(manifestField.options)) {
                          fieldDef.options = manifestField.options;
                        } else if (manifestField.options.source === 'handler' || manifestField.options.source === 'endpoint') {
                          try {
                            const optionsResponse = await fetch(
                              `/api/${currentWorkspace.slug}/${currentAgent.slug}/version/field-options?field=${fieldKey}`
                            );
                            if (optionsResponse.ok) {
                              const optionsData = await optionsResponse.json();
                              fieldDef.options = optionsData.options || [];
                            }
                          } catch (optionsError) {
                            log.warn('Failed to fetch field options', { fieldKey, errorObj: optionsError });
                            fieldDef.options = [];
                          }
                        }
                      }

                      definitions[fieldKey] = fieldDef;
                    }
                  }
                } catch (versionError) {
                  log.warn('Failed to load agent version dataSchema', { errorObj: versionError });
                }
              }

              set({ fieldDefinitions: definitions, isLoadingFieldDefinitions: false });
            } catch (error) {
              log.error('failed to reload field definitions', { errorObj: error });
              set({ isLoadingFieldDefinitions: false });
            }
          },

          invalidateUsersCache: () => {
            const queryClient = getQueryClient();
            queryClient.invalidateQueries({ queryKey: ['users'] });
          },

          invalidateViewsCache: () => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) return;

            const queryClient = getQueryClient();
            const queryKey = ["/api", currentWorkspace.slug, currentAgent.slug, "views"];
            queryClient.invalidateQueries({ queryKey });
            // Refetch active queries immediately to show new/updated data
            queryClient.refetchQueries({ queryKey });
          },

          reloadViews: throttle(async () => {
            const state = get();
            if (state.isLoadingViews) return;

            set({ isLoadingViews: true });
            log.info('reloading views');

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const queryClient = getQueryClient();
              const views = await queryClient.fetchQuery({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "views"],
                queryFn: async () => {
                  const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views`);
                  if (!response.ok) throw new Error('Failed to fetch views');
                  return response.json();
                },
              });
              // Context switched while the fetch was in flight — this answer is stale.
              if (get().currentAgent?.id !== currentAgent.id) {
                set({ isLoadingViews: false });
                return;
              }
              set({ views, viewsLoaded: true, isLoadingViews: false });
            } catch (error) {
              // TanStack Query throws CancelledError when queries are cancelled (e.g., rapid calls, component unmount)
              // These are expected and shouldn't be logged as errors
              const isCancelledError =
                error instanceof Error &&
                (error.message === 'CancelledError' ||
                  (error as any).silent === true ||
                  error.name === 'CancelledError');

              if (!isCancelledError) {
                log.error('failed to reload views', { errorObj: error });
              }
              set({ isLoadingViews: false });
            }
          }, 5000),

          reloadUser: async (userId: number) => {
            if (pendingUserRequests.has(userId)) {
              log.info('skipping duplicate user request', { userId });
              return null;
            }

            const state = get();
            if (state.isLoadingUser) return null;
            set({ isLoadingUser: true });

            try {
              pendingUserRequests.add(userId);

              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const queryClient = getQueryClient();
              const queryKey = userQueryKey(currentWorkspace.slug, currentAgent.slug, userId);

              // Remove cached data to ensure fresh fetch
              queryClient.removeQueries({ queryKey });

              const [dbUser, userHistoryResponse] = await Promise.all([
                queryClient.fetchQuery({
                  queryKey,
                  queryFn: async () => {
                    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${userId}`);
                    if (!response.ok) throw new Error('Failed to fetch user');
                    return response.json();
                  },
                }),
                get().reloadUserHistory(userId),
              ]);

              const user = flattenUser(dbUser);

              // Update user in Zustand state (for selectedUser/discussionUser)
              set((state) => ({
                isLoadingUser: false,
                selectedUser: state.selectedUserId === user.id ? user : state.selectedUser,
                discussionUser: state.discussionUserId === user.id ? user : state.discussionUser
              }));
              return user;
            } catch (error) {
              log.error('failed to reload user', { errorObj: error });
              set({ isLoadingUser: false });
              return null;
            } finally {
              pendingUserRequests.delete(userId);
            }
          },


          loadTakeoverUsers: async () => {
            const { currentWorkspace, currentAgent, currentViewId } = get();
            if (!currentWorkspace || !currentAgent) return;
            try {
              const filters = JSON.stringify([{ field: 'takeoverActive', operator: 'eq', value: 'true' }]);
              // View-limited roles may only list users through a view, so send the open one.
              const viewParam = currentViewId ? `&viewId=${encodeURIComponent(String(currentViewId))}` : '';
              const response = await fetch(
                `/api/${currentWorkspace.slug}/${currentAgent.slug}/users?filters=${encodeURIComponent(filters)}&pageSize=50${viewParam}`
              );
              if (!response.ok) return;
              const { users: takeoverUsersList } = await response.json();
              const takeoverUsers: Record<number, { recipientId: string; label: string }> = {};
              for (const u of takeoverUsersList) {
                takeoverUsers[u.id] = {
                  recipientId: u.recipientId || u.recipient_id || '',
                  label: u.data?.nombre || u.data?.name || u.recipientId || u.recipient_id || `User ${u.id}`,
                };
              }
              // Context switched while the fetch was in flight — this answer is stale.
              if (get().currentAgent?.id !== currentAgent.id) return;
              set({ takeoverUsers });
            } catch (error) {
              console.warn('[Takeover] Failed to load takeover users', error);
            }
          },

          reloadUserHistory: (userId: number) => {
            // A reload asked for mid-fetch may carry a row the running fetch missed, so it runs
            // again afterwards instead of being dropped.
            const inFlight = historyReloads.get(userId);
            if (inFlight) {
              inFlight.rerun = true;
              return inFlight.done;
            }

            const fetchAndMerge = async () => {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const idsAtFetchStart = new Set((get().userHistory[userId] || []).map((h) => h.id ?? 0));
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${userId}/history`);
              if (!response.ok) throw new Error('Failed to fetch user history');
              const { history, hasMore } = await response.json();

              const latestPage = history.map((h: any) => ({
                ...h,
                createdAt: h.createdAt ? new Date(h.createdAt) : null
              }));

              set((state) => {
                const { merged, keptOlder } = mergeHistoryPage({
                  existing: state.userHistory[userId] || [],
                  page: latestPage,
                  idsAtFetchStart,
                });

                return {
                  userHistory: {
                    ...state.userHistory,
                    [userId]: merged
                  },
                  historyHasMore: {
                    ...state.historyHasMore,
                    // Only update hasMore if we don't already have older history loaded
                    [userId]: keptOlder ? state.historyHasMore[userId] ?? hasMore : hasMore
                  },
                  ...(state.selectedUserId === userId ? { selectedUserHistory: merged } : {})
                };
              });
            };

            const entry = { rerun: false, done: Promise.resolve() };
            historyReloads.set(userId, entry);
            entry.done = (async () => {
              try {
                do {
                  entry.rerun = false;
                  await fetchAndMerge();
                } while (entry.rerun);
              } catch (error) {
                log.error('failed to reload user history', { errorObj: error, userId });
              } finally {
                historyReloads.delete(userId);
              }
            })();
            return entry.done;
          },

          loadMoreHistory: async (userId: number) => {
            const { currentWorkspace, currentAgent, historyLoadingMore, historyHasMore } = get();
            if (!currentWorkspace || !currentAgent) return;
            if (historyLoadingMore[userId]) return;
            if (historyHasMore[userId] === false) return;

            set((state) => ({
              historyLoadingMore: { ...state.historyLoadingMore, [userId]: true }
            }));

            try {
              const existing = get().userHistory[userId] || [];
              const oldestId = existing[0]?.id;
              const url = new URL(`/api/${currentWorkspace.slug}/${currentAgent.slug}/users/${userId}/history`, window.location.origin);
              if (oldestId) url.searchParams.set('before', String(oldestId));

              const response = await fetch(url.toString());
              if (!response.ok) throw new Error('Failed to fetch more history');
              const { history, hasMore } = await response.json();

              const olderPage = history.map((h: any) => ({
                ...h,
                createdAt: h.createdAt ? new Date(h.createdAt) : null
              }));

              set((state) => {
                const current = state.userHistory[userId] || [];
                const merged = [...olderPage, ...current];
                return {
                  userHistory: { ...state.userHistory, [userId]: merged },
                  historyHasMore: { ...state.historyHasMore, [userId]: hasMore },
                  historyLoadingMore: { ...state.historyLoadingMore, [userId]: false },
                  ...(state.selectedUserId === userId ? { selectedUserHistory: merged } : {})
                };
              });
            } catch (error) {
              log.error('failed to load more history', { errorObj: error, userId });
              // ChatDialog asks for more whenever the list is short and not loading, so a failure
              // must also mark the page exhausted or it retries at request speed. The next
              // reloadUserHistory resets hasMore from the server.
              set((state) => ({
                historyLoadingMore: { ...state.historyLoadingMore, [userId]: false },
                historyHasMore: { ...state.historyHasMore, [userId]: false }
              }));
            }
          },

          setCurrentWorkspace: (workspace) => set({ currentWorkspace: workspace }),
          setAvailableWorkspaces: (workspaces) => set({ availableWorkspaces: workspaces }),
          setCurrentAgent: (agent) => set({ currentAgent: agent }),
          setAvailableAgents: (agents) => set({ availableAgents: agents }),
          setCurrentWorkspaceRole: (role) => set({ currentWorkspaceRole: role }),
          setCurrentAgentRole: (role) => set({ currentAgentRole: role }),

          // One update on purpose: writing the lists before the workspace and
          // agent they belong to leaves the switcher showing another context.
          setWorkspaceContext: ({ workspace, workspaces, workspaceRole, workspaceAccess, agent, agents, agentRole, agentAccess }) => set({
            currentWorkspace: workspace,
            availableWorkspaces: workspaces,
            currentWorkspaceRole: workspaceRole,
            currentWorkspaceAccess: workspaceAccess ?? null,
            currentAgent: agent,
            availableAgents: agents,
            currentAgentRole: agentRole,
            currentAgentAccess: agentAccess ?? null,
            isWorkspaceContextLoaded: true,
          }),

          setAgentVersionPages: (pages) => set({ agentVersionPages: pages }),

          loadAgentVersion: async (options) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              set({ agentVersionPages: null, isLoadingAgentVersionPages: false });
              return;
            }

            // Runs on every route change; the sidebar keeps showing the pages it has while this
            // refreshes, so the flag only means "a load is in flight", not "nothing to show".
            set({ isLoadingAgentVersionPages: true });

            try {
              const queryClient = getQueryClient();
              const data = await queryClient.fetchQuery({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "version"],
                queryFn: async () => {
                  const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/version`);
                  if (!response.ok) {
                    throw new Error(`Failed to fetch agent version: ${response.status}`);
                  }
                  return response.json();
                },
                // The client default is staleTime: Infinity, so a route change reuses the cached answer.
                ...(options?.fresh ? { staleTime: 0 } : {}),
              });
              // Context switched while the fetch was in flight — this answer is stale.
              if (get().currentAgent?.id !== currentAgent.id) {
                set({ isLoadingAgentVersionPages: false });
                return;
              }
              set({
                agentVersionPages: data.pages || [],
                agentVersionManifestId: data.manifest?.id ?? null,
                isLoadingAgentVersionPages: false,
              });
            } catch (error) {
              log.error('failed to load agent version', { errorObj: error });
              set({ agentVersionPages: [], agentVersionManifestId: null, isLoadingAgentVersionPages: false });
            }
          },

          switchWorkspace: async (workspaceSlug: string) => {
            const currentWorkspace = get().currentWorkspace;
            if (currentWorkspace?.slug === workspaceSlug) return;

            // The old page stays on screen for as long as the navigation takes,
            // so drop the lists belonging to the context we are leaving.
            set({ availableWorkspaces: [], availableAgents: [], isWorkspaceContextLoaded: false });

            // Do a full page navigation to trigger AppStateLoader initialization
            // This ensures all data is properly loaded for the new workspace context
            window.location.href = `/${workspaceSlug}`;
          },

          switchAgent: async (agentSlug: string) => {
            const currentWorkspace = get().currentWorkspace;
            if (!currentWorkspace) throw new Error('No workspace selected');

            const currentAgent = get().currentAgent;
            if (currentAgent?.slug === agentSlug) return;

            set({ availableWorkspaces: [], availableAgents: [], isWorkspaceContextLoaded: false });

            // Do a full page navigation to trigger AppStateLoader initialization
            // This ensures all data is properly loaded for the new agent context
            window.location.href = `/${currentWorkspace.slug}/${agentSlug}`;
          },

          loadWorkspaceData: async () => {
            // Workspace-level data is minimal in multi-tenancy
            // Most data is agent-scoped
            const { currentWorkspace } = get();
            if (!currentWorkspace) return;

            try {
              // Currently no workspace-level data to load
              // Views, prompts, and configs are agent-scoped
              log.info('workspace data loaded', { workspaceId: currentWorkspace.id });
            } catch (error) {
              log.error('failed to load workspace data', { errorObj: error });
            }
          },

          loadAgentData: async () => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) return;

            try {
              const queryClient = getQueryClient();
              const [views, schema, membersData, versionData] = await Promise.all([
                queryClient.fetchQuery({
                  queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "views"],
                  queryFn: async () => {
                    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views`);
                    if (!response.ok) throw new Error('Failed to fetch views');
                    return response.json();
                  },
                }),
                queryClient.fetchQuery({
                  queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "schema"],
                  queryFn: async () => {
                    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/schema`);
                    if (!response.ok) throw new Error('Failed to fetch schema');
                    return response.json();
                  },
                }),
                queryClient.fetchQuery({
                  queryKey: ["/api/workspaces", currentWorkspace.slug, "members", currentAgent.id],
                  queryFn: async () => {
                    const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members?agentId=${currentAgent.id}`);
                    if (!response.ok) {
                      // Members fetch is optional (may fail for permission reasons)
                      return { members: [] };
                    }
                    return response.json();
                  },
                }).catch(() => {
                  // Members fetch is optional
                  return { members: [] };
                }),
                // Load agent version pages in parallel with views (fetch directly, set together with views)
                queryClient.fetchQuery({
                  queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "version"],
                  queryFn: async () => {
                    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/version`);
                    if (!response.ok) throw new Error('Failed to fetch agent version');
                    return response.json();
                  },
                }).catch(() => {
                  // Version fetch is optional (agent may not have pages)
                  return { pages: null };
                }),
              ]);

              // Members fetch is optional (may fail for permission reasons)
              const members = membersData?.members || [];

              // Pass schema to avoid redundant fetch, and members data to avoid redundant fetch
              const fieldDefinitions = await getFieldDefinitions(schema);

              // Context switched while the fetches were in flight — this answer is stale.
              if (get().currentAgent?.id !== currentAgent.id) return;

              // Note: userHistory remains {} and is loaded on-demand via reloadUserHistory(userId)
              // when a user is selected. See existing pattern in setSelectedUserId() action.
              set({
                views,
                fieldDefinitions,
                agentVersionPages: versionData?.pages || null, // Set pages together with views
                agentVersionManifestId: versionData?.manifest?.id ?? null,
              });

              log.info('agent data loaded', {
                workspaceId: currentWorkspace.id,
                agentId: currentAgent.id,
                viewsCount: views.length,
                membersCount: members.length
              });

              await get().fetchActions();
            } catch (error) {
              log.error('failed to load agent data', { errorObj: error });
              set({ views: [], fieldDefinitions: {} });
            }
          },

          fetchActions: async (trigger?: 'bulk' | 'single' | 'viewEntry', options?: { force?: boolean }) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              log.warn('cannot fetch actions: no workspace/agent context');
              return;
            }

            // Coalesce the duplicate fetches that page load produces (AppStateLoader and
            // loadAgentData both ask). A `force` caller is refreshing *after* a write, so its
            // read must not be answered by a request that started before that write —
            // dropping it is exactly how the action menus went stale after an agent was saved.
            if (get().isLoadingActions && !options?.force) {
              log.info('skipping duplicate actions fetch');
              return;
            }

            const seq = ++actionsFetchSeq;
            set({ isLoadingActions: true });

            try {
              const triggers: ('bulk' | 'single' | 'viewEntry')[] = trigger ? [trigger] : ['bulk', 'single', 'viewEntry'];
              const fetchPromises = triggers.map(async (t) => {
                try {
                  const response = await fetch(
                    `/api/${currentWorkspace.slug}/${currentAgent.slug}/actions?trigger=${t}`
                  );
                  if (response.ok) {
                    const data = await response.json();
                    return { trigger: t, actions: data.actions || [] };
                  }
                  return { trigger: t, actions: [] };
                } catch (error) {
                  log.error(`failed to fetch ${t} actions`, { errorObj: error });
                  return { trigger: t, actions: [] };
                }
              });

              const results = await Promise.all(fetchPromises);
              const allActions: ActionConfig[] = [];
              const bulkActions: ActionConfig[] = [];
              const singleActions: ActionConfig[] = [];
              const viewEntryActions: ActionConfig[] = [];

              results.forEach(({ trigger, actions }) => {
                allActions.push(...actions);
                if (trigger === 'bulk') {
                  bulkActions.push(...actions);
                } else if (trigger === 'single') {
                  singleActions.push(...actions);
                } else if (trigger === 'viewEntry') {
                  viewEntryActions.push(...actions);
                }
              });

              // A newer fetch has already been issued, or the context switched mid-flight —
              // either way this answer is stale.
              if (seq !== actionsFetchSeq || get().currentAgent?.id !== currentAgent.id) {
                if (seq === actionsFetchSeq) set({ isLoadingActions: false });
                return;
              }

              set({
                actions: allActions,
                bulkActions,
                singleActions,
                viewEntryActions,
                isLoadingActions: false
              });

              log.info('actions fetched', {
                total: allActions.length,
                bulk: bulkActions.length,
                single: singleActions.length,
                viewEntry: viewEntryActions.length
              });
            } catch (error) {
              log.error('failed to fetch actions', { errorObj: error });
              if (seq !== actionsFetchSeq) return;
              set({
                actions: [],
                bulkActions: [],
                singleActions: [],
                viewEntryActions: [],
                isLoadingActions: false
              });
            }
          },

          getActionsByTrigger: (trigger: 'bulk' | 'single' | 'viewEntry') => {
            const state = get();
            if (trigger === 'bulk') return state.bulkActions;
            if (trigger === 'single') return state.singleActions;
            if (trigger === 'viewEntry') return state.viewEntryActions;
            return [];
          },

          setActionRunning: (definitionId: string, running: boolean) => {
            const prev = get().runningActionDefinitionIds;
            const next = new Set(prev);
            if (running) next.add(definitionId);
            else next.delete(definitionId);
            set({ runningActionDefinitionIds: next });
          },

          setOnlineUsers: (users: Record<string, PresenceState>) => {
            const usersByRow: Record<number, PresenceState[]> = {};
            for (const user of Object.values(users)) {
              if (user.selectedUserId !== null && user.selectedUserId !== undefined) {
                const rowId = user.selectedUserId;
                if (!usersByRow[rowId]) {
                  usersByRow[rowId] = [];
                }
                usersByRow[rowId].push(user);
              }
            }
            set({ onlineUsers: users, usersByRow });
          },

          setUsersByRow: (mapping: Record<number, PresenceState[]>) => {
            set({ usersByRow: mapping });
          },

          setActiveCellEdit: (key: string, edit: ActiveCellEdit | null) => {
            set((state) => {
              const next = { ...state.activeCellEdits };
              if (edit) {
                next[key] = edit;
              } else {
                delete next[key];
              }
              return { activeCellEdits: next };
            });
          },

          setChatTypingUser: (userId: number, user: PresenceState | null) => {
            set((state) => {
              const next = { ...state.chatTypingUsers };
              if (user) {
                const existing = next[userId] || [];
                if (!existing.find(u => u.systemUserId === user.systemUserId)) {
                  next[userId] = [...existing, user];
                }
              } else {
                delete next[userId];
              }
              return { chatTypingUsers: next };
            });
          },

          setSimulatedTyping: (userId: number, typist: SimulatedTypist | null) => {
            set((state) => {
              const next = { ...state.simulatedTyping };
              if (typist) next[userId] = typist;
              else delete next[userId];
              return { simulatedTyping: next };
            });
          },

          clearAllCollabState: () => {
            set({
              onlineUsers: {},
              usersByRow: {},
              activeCellEdits: {},
              chatTypingUsers: {},
              simulatedTyping: {},
            });
          },

          inviteWorkspaceMember: async (email: string, role: 'workspace_owner' | 'workspace_member', agentAssignments?: Array<{ agentId: number; role: 'agent_superadmin' | 'agent_admin' | 'agent_member' }>) => {
            const { currentWorkspace, loadWorkspaceMembers } = get();
            if (!currentWorkspace) {
              throw new Error('No workspace context');
            }

            const body: any = { email, workspaceRole: role };
            if (agentAssignments && agentAssignments.length > 0) {
              body.agentAssignments = agentAssignments;
            }

            const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });

            if (!response.ok) {
              const error = await response.json();
              const errorObj = new Error(error.error || 'Failed to invite member');
              // Attach status code to error for frontend error handling
              (errorObj as any).statusCode = response.status;
              throw errorObj;
            }

            await loadWorkspaceMembers();
          },

          updateWorkspaceMemberRole: async (memberId: string, role: 'workspace_owner' | 'workspace_member') => {
            const { currentWorkspace, loadWorkspaceMembers } = get();
            if (!currentWorkspace) {
              throw new Error('No workspace context');
            }

            const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${memberId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ role }),
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.error || 'Failed to update member role');
            }

            await loadWorkspaceMembers();
          },

          removeWorkspaceMember: async (memberId: string) => {
            const { currentWorkspace, loadWorkspaceMembers } = get();
            if (!currentWorkspace) {
              throw new Error('No workspace context');
            }

            const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${memberId}`, {
              method: 'DELETE',
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.error || 'Failed to remove member');
            }

            await loadWorkspaceMembers();
          },

          fetchPromptTypes: async () => {
            set({ isLoadingPromptTypes: true });
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }
            try {
              const queryClient = getQueryClient();
              const { types } = await queryClient.fetchQuery({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "prompt-types"],
                queryFn: async () => {
                  const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/prompt-types`);
                  if (!response.ok) throw new Error('Failed to fetch prompt types');
                  return response.json();
                },
              });

              // Context switched while the fetch was in flight — this answer is stale.
              if (get().currentAgent?.id !== currentAgent.id) {
                set({ isLoadingPromptTypes: false });
                return;
              }

              set({
                promptTypes: types,
                isLoadingPromptTypes: false
              });

              const state = get();
              if (!state.selectedPromptType && types.length > 0) {
                set({ selectedPromptType: types[0].type });
              }
            } catch (error) {
              log.error('failed to fetch prompt types', { errorObj: error });
              set({ isLoadingPromptTypes: false });
            }
          },

          reorderPromptTypes: async (orderedTypeKeys: string[]) => {
            const { currentWorkspace, currentAgent, promptTypes } = get();
            if (!currentWorkspace || !currentAgent) return;

            // Optimistically reorder local state, reindexing sortOrder to match.
            const prev = promptTypes;
            const byType = new Map(promptTypes.map((t) => [t.type, t]));
            const reordered = orderedTypeKeys
              .map((key) => byType.get(key))
              .filter((t): t is PromptTypeMetadata => t !== undefined)
              .map((t, index) => ({ ...t, sortOrder: index }));

            // Bail if the order didn't actually change (e.g. dropped in place).
            if (reordered.every((t, i) => t.type === prev[i]?.type)) return;

            set({ promptTypes: reordered });

            const typeOrders = reordered.map((t, index) => ({ type: t.type, sortOrder: index }));
            try {
              const response = await fetch(
                `/api/${currentWorkspace.slug}/${currentAgent.slug}/prompt-types`,
                {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ typeOrders }),
                },
              );
              if (!response.ok) throw new Error('Failed to update prompt type order');
              await reloadWrittenVersion(await response.json());
            } catch (error) {
              log.error('failed to reorder prompt types', { errorObj: error });
              set({ promptTypes: prev }); // revert optimistic update
            }
          },

          openCodePanel: (file?: string) => {
            set((state) => {
              const selectedCodeFile = file ?? state.selectedCodeFile ?? 'manifest.json';
              return {
                selectedCodeFile,
                selectedPromptType: promptTypeOfPath(selectedCodeFile) ?? state.selectedPromptType,
                layout: { ...state.layout, codePanelCollapsed: false },
              };
            });
          },

          createPromptType: async (data) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }
            try {
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/prompt-types`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
              });

              if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create prompt type');
              }
              const created = await response.json();
              await reloadWrittenVersion(created);

              // The prompt-types query is cached with staleTime: Infinity, so fetchPromptTypes
              // would otherwise return a stale list missing the new type (and its `kind`),
              // leaving the editor to fall back to 'system'. Invalidate before refetching.
              getQueryClient().invalidateQueries({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "prompt-types"],
              });

              await get().fetchPromptTypes();

              log.info('prompt type created', { type: data.type });
              return { agentVersionId: typeof created.agentVersionId === 'number' ? created.agentVersionId : null };
            } catch (error) {
              log.error('failed to create prompt type', { errorObj: error });
              throw error;
            }
          },

          deletePromptType: async (type: string) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }
            try {
              // Read before the refetch removes it: whatever moves up into this slot is where the
              // editor should land.
              const orderBeforeDelete = get().promptTypes.map((t) => t.type);
              const deletedIndex = orderBeforeDelete.indexOf(type);
              const wasSelected = get().selectedPromptType === type;

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/prompt-types/${type}`, {
                method: 'DELETE',
              });

              if (!response.ok) throw new Error('Failed to delete prompt type');
              await reloadWrittenVersion(await response.json());

              // Invalidate the (staleTime: Infinity) prompt-types cache so the deleted
              // type doesn't linger in the refetched list.
              getQueryClient().invalidateQueries({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "prompt-types"],
              });

              await get().fetchPromptTypes();

              if (wasSelected) {
                const remaining = get().promptTypes;
                // Whatever now sits at the deleted index is the next one down; deleting the last
                // entry falls back to the one above it.
                const next =
                  (deletedIndex >= 0 ? remaining[deletedIndex] : undefined) ??
                  remaining[remaining.length - 1];

                const showingDeleted = get().selectedCodeFile === promptPath(type);
                set({
                  selectedPromptType: next?.type ?? null,
                  ...(showingDeleted && { selectedCodeFile: next ? promptPath(next.type) : 'manifest.json' }),
                });
              }

              log.info('prompt type deleted', { type });
            } catch (error) {
              log.error('failed to delete prompt type', { errorObj: error });
              throw error;
            }
          },

          updateTypeMetadata: async (type: string, metadata) => {
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }

            try {
              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/prompt-types/${type}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(metadata),
              });

              if (!response.ok) throw new Error('Failed to update type metadata');

              await reloadWrittenVersion(await response.json());

              // Invalidate the (staleTime: Infinity) prompt-types cache so the refetch
              // reflects the updated metadata (kind, description, etc.).
              getQueryClient().invalidateQueries({
                queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "prompt-types"],
              });

              await get().fetchPromptTypes();

              // Type-level metadata is half of a synthesized action: `kind` decides whether the
              // type is an action at all, and the name, description and icon are what it's
              // labelled with.
              await get().fetchActions(undefined, { force: true });

              log.info('prompt type metadata updated', { type });
            } catch (error) {
              log.error('failed to update type metadata', { errorObj: error });
              throw error;
            }
          },

          removeFieldDefinition: async (fieldName: string) => {
            set({ isRemovingFieldDefinition: true, removeFieldDefinitionError: null });

            try {
              if (!fieldName) {
                throw new Error('Field name is required');
              }

              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/schema?field=${encodeURIComponent(fieldName)}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove field');
              }

              await get().reloadFieldDefinitions();
              set({ isRemovingFieldDefinition: false });
              log.info('field definition removed successfully', { fieldName });
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to remove field';
              set({ removeFieldDefinitionError: errorMessage, isRemovingFieldDefinition: false });
              log.error('failed to remove field definition', { errorObj: error, fieldName });
              throw error;
            }
          },

          checkAdminAccess: async () => {
            set({ isLoadingAdminAccess: true });
            const { currentWorkspace, currentAgent } = get();
            if (!currentWorkspace || !currentAgent) {
              throw new Error('No workspace/agent context');
            }
            try {
              const supabase = createClient();
              const { data: { user } } = await supabase.auth.getUser();

              if (user?.email && user?.id) {
                const queryClient = getQueryClient();
                const data = await queryClient.fetchQuery({
                  queryKey: ["/api", currentWorkspace.slug, currentAgent.slug, "admin", "check-access"],
                  queryFn: async () => {
                    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/admin/check-access`);
                    if (!response.ok) {
                      throw new Error('Failed to check access');
                    }
                    return response.json();
                  },
                });

                const currentUser: CurrentUser = {
                  id: data.userId || user.id,
                  email: data.email || user.email,
                  role: data.role || 'user',
                  name: data.name,
                  avatarUrl: data.avatarUrl,
                };

                set({
                  currentUser,
                  hasAdminAccess: data.hasAccess
                });

              } else {
                set({
                  currentUser: null,
                  hasAdminAccess: false
                });
              }
            } catch (error) {
              log.error('failed to check admin access', { errorObj: error });
              set({
                currentUser: null,
                hasAdminAccess: false
              });
            } finally {
              set({ isLoadingAdminAccess: false });
            }
          },

          reloadAuthUsers: async () => {
            const state = get();
            if (state.isLoadingAuthUsers || !state.hasAdminAccess) return;

            set({ isLoadingAuthUsers: true });
            try {
              const response = await fetch(`/api/admin/auth-users`);
              if (!response.ok) throw new Error('Failed to fetch auth users');

              const data = await response.json();
              set({
                authUsers: data.users,
                totalAuthUsersCount: data.total,
                isLoadingAuthUsers: false
              });
              log.info('auth users reloaded', { userCount: data.users.length });
            } catch (error) {
              log.error('failed to reload auth users', { errorObj: error });
              set({ isLoadingAuthUsers: false });
            }
          },

          createAuthUser: async (userData: AuthUserCreateRequest): Promise<AuthUser> => {
            set({ isCreatingAuthUser: true });
            try {
              const response = await fetch(`/api/admin/auth-users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create user');
              }

              const data = await response.json();

              set((state) => ({
                authUsers: [data.user, ...state.authUsers],
                totalAuthUsersCount: state.totalAuthUsersCount + 1,
                isCreatingAuthUser: false
              }));

              log.info('auth user created successfully', { email: data.user.email });
              return data.user;
            } catch (error) {
              set({ isCreatingAuthUser: false });
              log.error('failed to create auth user', { errorObj: error });
              throw error;
            }
          },

          updateAuthUser: async (id: string, userData: AuthUserUpdateRequest): Promise<AuthUser> => {
            set({ isUpdatingAuthUser: id });
            try {
              const response = await fetch(`/api/admin/auth-users/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update user');
              }

              const data = await response.json();

              set((state) => ({
                authUsers: state.authUsers.map(user => user.id === id ? data.user : user),
                selectedAuthUser: state.selectedAuthUserId === id ? data.user : state.selectedAuthUser,
                isUpdatingAuthUser: null
              }));

              log.info('auth user updated successfully', { id, email: data.user.email });
              return data.user;
            } catch (error) {
              set({ isUpdatingAuthUser: null });
              log.error('failed to update auth user', { errorObj: error, id });
              throw error;
            }
          },

          deleteAuthUser: async (id: string): Promise<boolean> => {
            set({ isDeletingAuthUser: id });
            try {
              const response = await fetch(`/api/admin/auth-users/${id}`, {
                method: 'DELETE',
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete user');
              }

              set((state) => ({
                authUsers: state.authUsers.filter(user => user.id !== id),
                totalAuthUsersCount: state.totalAuthUsersCount - 1,
                selectedAuthUser: state.selectedAuthUserId === id ? null : state.selectedAuthUser,
                selectedAuthUserId: state.selectedAuthUserId === id ? null : state.selectedAuthUserId,
                isDeletingAuthUser: null
              }));

              log.info('auth user deleted successfully', { id });
              return true;
            } catch (error) {
              set({ isDeletingAuthUser: null });
              log.error('failed to delete auth user', { errorObj: error, id });
              throw error;
            }
          },

          resetAuthUserPassword: async (id: string): Promise<string> => {
            set({ isResettingAuthUserPassword: id });
            try {
              const response = await fetch(`/api/admin/auth-users/${id}/reset`, {
                method: 'POST',
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to reset password');
              }

              const data = await response.json();
              set({ isResettingAuthUserPassword: null });

              log.info('password reset initiated successfully', { id });
              return data.message;
            } catch (error) {
              set({ isResettingAuthUserPassword: null });
              log.error('failed to reset password for auth user', { errorObj: error, id });
              throw error;
            }
          },

          setSelectedAuthUserId: (id: string | null) => {
            set((state) => {
              const selectedUser = id ? state.authUsers.find(user => user.id === id) || null : null;
              return {
                selectedAuthUserId: id,
                selectedAuthUser: selectedUser
              };
            });
          },

          loadWorkspaceMembers: async (agentId?: number) => {
            const state = get();
            const { currentWorkspace, isLoadingWorkspaceMembers } = state;
            if (!currentWorkspace || isLoadingWorkspaceMembers) return;

            set({ isLoadingWorkspaceMembers: true, workspaceMembersError: null });

            try {
              // Always fetch unfiltered workspace members (filter client-side when needed)
              const url = `/api/workspaces/${currentWorkspace.slug}/members`;

              const response = await fetch(url);
              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to load members');
              }

              const { members } = await response.json();
              set({ workspaceMembers: members, isLoadingWorkspaceMembers: false });
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to load workspace members';
              log.error('failed to load workspace members', { errorObj: error });
              set({
                isLoadingWorkspaceMembers: false,
                workspaceMembersError: errorMessage
              });
            }
          },

          clearWorkspaceMembers: () => {
            set({
              workspaceMembers: [],
              isLoadingWorkspaceMembers: false,
              workspaceMembersError: null
            });
          },

          updateWorkspaceUserRole: async (userId: string, role: 'workspace_owner' | 'workspace_member') => {
            const state = get();
            const { currentWorkspace } = state;

            if (!currentWorkspace) {
              throw new Error('No workspace available');
            }

            try {
              const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${userId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ workspaceRole: role }),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update workspace role');
              }

              const data = await response.json();

              set((state) => ({
                workspaceMembers: state.workspaceMembers.map(user =>
                  user.systemUserId === userId
                    ? { ...user, workspaceMembership: { ...user.workspaceMembership, role } }
                    : user
                ),
              }));

              log.info('workspace user role updated', { userId, role });
            } catch (error) {
              log.error('failed to update workspace user role', { errorObj: error, userId });
              throw error;
            }
          },

          addUserToAgent: async (userId: string, agentId: number, role: 'agent_superadmin' | 'agent_admin' | 'agent_member') => {
            const state = get();
            const { currentWorkspace, availableAgents } = state;

            if (!currentWorkspace) {
              throw new Error('No workspace available');
            }

            // SECURITY: Validate agentId belongs to current workspace
            const agent = availableAgents.find(a => a.id === agentId);
            if (!agent || agent.workspaceId !== currentWorkspace.id) {
              throw new Error('Agent does not belong to this workspace');
            }

            try {
              const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${userId}/agents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentId, role }),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add user to agent');
              }

              const data = await response.json();

              set((state) => ({
                workspaceMembers: state.workspaceMembers.map(user => {
                  if (user.systemUserId === userId) {
                    return {
                      ...user,
                      agentMemberships: [
                        ...user.agentMemberships,
                        {
                          id: data.agentMember.id,
                          agentId,
                          agentName: agent.name,
                          agentSlug: agent.slug,
                          role,
                          invitationStatus: 'accepted' as const,
                          invitedBy: data.agentMember.invitedBy,
                          invitedAt: new Date(data.agentMember.invitedAt),
                          acceptedAt: new Date(data.agentMember.acceptedAt),
                        }
                      ],
                      agentCount: user.agentCount + 1
                    };
                  }
                  return user;
                }),
              }));

              log.info('user added to agent', { userId, agentId, role });
            } catch (error) {
              log.error('failed to add user to agent', { errorObj: error, userId, agentId });
              throw error;
            }
          },

          removeUserFromAgent: async (userId: string, agentId: number) => {
            const state = get();
            const { currentWorkspace, availableAgents } = state;

            if (!currentWorkspace) {
              throw new Error('No workspace available');
            }

            // SECURITY: Validate agentId belongs to current workspace
            const agent = availableAgents.find(a => a.id === agentId);
            if (!agent || agent.workspaceId !== currentWorkspace.id) {
              throw new Error('Agent does not belong to this workspace');
            }

            try {
              const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${userId}/agents/${agentId}`, {
                method: 'DELETE',
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to remove user from agent');
              }

              set((state) => ({
                workspaceMembers: state.workspaceMembers.map(user => {
                  if (user.systemUserId === userId) {
                    return {
                      ...user,
                      agentMemberships: user.agentMemberships.filter(m => m.agentId !== agentId),
                      agentCount: user.agentCount - 1
                    };
                  }
                  return user;
                }),
              }));

              log.info('user removed from agent', { userId, agentId });
            } catch (error) {
              log.error('failed to remove user from agent', { errorObj: error, userId, agentId });
              throw error;
            }
          },

          updateUserAgentRole: async (userId: string, agentId: number, role: 'agent_superadmin' | 'agent_admin' | 'agent_member') => {
            const state = get();
            const { currentWorkspace, availableAgents } = state;

            if (!currentWorkspace) {
              throw new Error('No workspace available');
            }

            // SECURITY: Validate agentId belongs to current workspace
            const agent = availableAgents.find(a => a.id === agentId);
            if (!agent || agent.workspaceId !== currentWorkspace.id) {
              throw new Error('Agent does not belong to this workspace');
            }

            try {
              const response = await fetch(`/api/workspaces/${currentWorkspace.slug}/members/${userId}/agents/${agentId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role }),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update agent role');
              }

              set((state) => ({
                workspaceMembers: state.workspaceMembers.map(user => {
                  if (user.systemUserId === userId) {
                    return {
                      ...user,
                      agentMemberships: user.agentMemberships.map(m =>
                        m.agentId === agentId
                          ? { ...m, role }
                          : m
                      )
                    };
                  }
                  return user;
                }),
              }));

              log.info('user agent role updated', { userId, agentId, role });
            } catch (error) {
              log.error('failed to update user agent role', { errorObj: error, userId, agentId });
              throw error;
            }
          },

          reloadStripePrices: async () => {
            const state = get();
            if (state.isLoadingStripePrices) return;

            // Get current workspace slug
            const { currentWorkspace } = state;
            if (!currentWorkspace) {
              throw new Error('No workspace available');
            }

            set({ isLoadingStripePrices: true });
            try {
              const response = await fetch(`/api/${currentWorkspace.slug}/billing/prices`);

              if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                  throw new Error('Unauthorized: Superadmin access required');
                }
                throw new Error('Failed to fetch Stripe prices');
              }

              const data = await response.json();
              set({
                stripePrices: data.prices,
                isLoadingStripePrices: false
              });
              log.info('stripe prices reloaded', { priceCount: data.prices.length });
            } catch (error) {
              log.error('failed to reload stripe prices', { errorObj: error });
              set({ isLoadingStripePrices: false });
              throw error;
            }
          },

          runEvaluation: async (userId, options) => {
            if (!userId) {
              log.error('Cannot evaluate: missing userId');
              return { runUserId: null, error: 'Missing user id' };
            }

            set({ isEvaluating: true, evaluationResult: null });

            try {
              const { currentWorkspace, currentAgent } = get();
              if (!currentWorkspace || !currentAgent) {
                throw new Error('No workspace/agent context');
              }

              const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/actions/execute`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  actionId: 'run-evaluation',
                  trigger: 'single',
                  userId: userId,
                  payload: {
                    referenceUserIds: options?.referenceUserIds ?? [],
                    ...(options?.versionId ? { versionId: options.versionId } : {}),
                  },
                }),
              });

              if (!response.ok) {
                throw new Error(`Evaluation request failed with status: ${response.status}`);
              }

              const result = await response.json();
              const runUserId: number | null = typeof result.data?.runUserId === 'number' && result.data.runUserId > 0
                ? result.data.runUserId
                : null;
              getQueryClient().invalidateQueries({ queryKey: EVAL_RUNS_ROOT });
              // The user query never goes stale on its own, so drop the run's cached copy before it is opened.
              if (runUserId) {
                getQueryClient().removeQueries({
                  queryKey: userQueryKey(currentWorkspace.slug, currentAgent.slug, runUserId),
                });
              }
              if (!result.success || !result.data) {
                set({ isEvaluating: false, evaluationResult: null });
                log.warn('evaluation returned an error', { userId, runUserId });
                return { runUserId, error: result.message || 'Invalid response from evaluation action' };
              }

              set({
                isEvaluating: false,
                evaluationResult: {
                  pass: result.data.pass,
                  reason: result.data.reason,
                  confidence: result.data.confidence,
                  regeneratedResponse: result.data.regeneratedResponse,
                }
              });
              log.info('evaluation completed', { userId, runUserId, pass: result.data.pass });
              return { runUserId };
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
              set({ isEvaluating: false, evaluationResult: null });
              log.error('evaluation failed', { errorObj: error, userId });
              return { runUserId: null, error: errorMessage };
            }
          },
        }),
        {
          name: 'apprentice-storage',
          partialize: (state) => ({
            sidebarOpen: state.sidebarOpen,
            columnWidths: state.columnWidths,
            columnOrder: state.columnOrder,
            layout: state.layout,
            notificationPrefs: state.notificationPrefs,
            // Last viewed workspace/agent (IDs only - full objects loaded from API)
            currentWorkspace: state.currentWorkspace ? { id: state.currentWorkspace.id, slug: state.currentWorkspace.slug } : null,
            currentAgent: state.currentAgent
              ? { id: state.currentAgent.id, slug: state.currentAgent.slug, workspaceId: state.currentAgent.workspaceId }
              : null,
            // NOT persisted:
            // - views: contain large appState blobs that exceed localStorage quota
            // - fieldDefinitions: contains React elements that can't serialize
            // - users, _viewCache: TanStack Query manages user caching
            // - userHistory, userComments: can grow very large, fetched on demand
          }),
          onRehydrateStorage: () => async (state) => {
            try {
              // Always start with the code panel collapsed and setup mode off (don't persist these across sessions)
              if (state?.layout) {
                state.layout.codePanelCollapsed = true;
                state.layout.apprenticeMode = 'sidebar';
                state.layout.assistantFullScreen = false;
                // Backfill pixel-width keys for users whose persisted layout predates the rename
                if (typeof state.layout.leftSidebarWidth !== 'number' || Number.isNaN(state.layout.leftSidebarWidth)) {
                  state.layout.leftSidebarWidth = DEFAULT_LEFT_SIDEBAR_WIDTH;
                }
                if (typeof state.layout.rightSidebarWidth !== 'number' || Number.isNaN(state.layout.rightSidebarWidth)) {
                  state.layout.rightSidebarWidth = DEFAULT_RIGHT_SIDEBAR_WIDTH;
                }
                if (typeof state.layout.userPanelWidth !== 'number' || Number.isNaN(state.layout.userPanelWidth)) {
                  state.layout.userPanelWidth = DEFAULT_USER_PANEL_WIDTH;
                }
                // The discussion panel used to share userPanelWidth; start it where the user left that.
                if (typeof state.layout.discussionPanelWidth !== 'number' || Number.isNaN(state.layout.discussionPanelWidth)) {
                  state.layout.discussionPanelWidth = state.layout.userPanelWidth;
                }
                const legacy = state.layout as LayoutState & { promptEditPanelHeight?: number; promptEditPanelCollapsed?: boolean };
                if (typeof state.layout.codePanelHeight !== 'number' || Number.isNaN(state.layout.codePanelHeight)) {
                  state.layout.codePanelHeight =
                    typeof legacy.promptEditPanelHeight === 'number' ? legacy.promptEditPanelHeight : DEFAULT_CODE_PANEL_HEIGHT;
                }
                delete legacy.promptEditPanelHeight;
                delete legacy.promptEditPanelCollapsed;
              }

              if (typeof window === 'undefined') {
                return;
              }

              const supabase = await createClient();

              const { data: { session } } = await supabase.auth.getSession();

              if (session?.access_token) {
                try {
                  supabase.realtime.setAuth(session.access_token);
                } catch (error) {
                  // Silently ignore setAuth errors - realtime will still work for public tables
                }
              }

              const { data: { user: authUser } } = await supabase.auth.getUser();
              console.log('[Realtime] Auth context for realtime', {
                authUid: authUser?.id,
                email: authUser?.email,
                isAuthenticated: !!authUser
              });

              // Generate a unique channel ID to avoid collisions between browser tabs
              const instanceId = Date.now().toString(36) + Math.random().toString(36).substring(2);
              console.log('[Realtime] Initializing Supabase subscriptions', { instanceId });

              // Calling channel.subscribe() again after CHANNEL_ERROR is a silent no-op: the
              // library only performs a join when the channel state is `closed`, and a failed
              // channel sits in `errored`. Recovery must unsubscribe first, then rejoin (the
              // postgres_changes bindings survive on the channel object). `onRecovered` runs
              // after a rejoin that follows a failure, so callers can refetch whatever events
              // were missed while the channel was down.
              // Channels closed on purpose (agent switch, unload) report CLOSED like a failure
              // would; the recovery loop must not bring them back.
              const retiredChannels = new WeakSet<ReturnType<typeof supabase.channel>>();

              const subscribeWithRecovery = (
                channel: ReturnType<typeof supabase.channel>,
                name: string,
                onRecovered?: () => void,
                isRetry = false
              ) => {
                // Each resubscribe registers a fresh status callback on the channel; the old
                // closures keep firing on socket events, so they mark themselves settled and
                // go quiet instead of scheduling duplicate retries.
                let settled = false;
                channel.subscribe((status: string) => {
                  if (settled || retiredChannels.has(channel)) return;
                  if (status === 'SUBSCRIBED') {
                    if (isRetry) {
                      console.log('[Realtime] channel resubscribed', { name });
                      onRecovered?.();
                    }
                    return;
                  }
                  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    settled = true;
                    console.warn('[Realtime] channel subscription failed, resubscribing in 5s', { name, status });
                    setTimeout(() => {
                      if (retiredChannels.has(channel)) return;
                      // The library's own rejoin timer may have already recovered the channel.
                      if (channel.state === 'joined') {
                        onRecovered?.();
                        return;
                      }
                      channel.unsubscribe().then(() => subscribeWithRecovery(channel, name, onRecovered, true));
                    }, 5000);
                  }
                });
              };

              // Table/internal columns the UI relies on that never appear in fieldDefinitions.
              // Takeover fields are member-visible by design (they drive the takeover banner).
              // recipientId is intentionally absent: it is restrictable, so it only passes the
              // scrub when the role-filtered schema includes it as a fieldDefinition.
              const REALTIME_INTERNAL_KEYS = new Set([
                'id', 'agentId', 'workspaceId', 'createdAt', 'updatedAt', 'lastUpdatedAt',
                'lastResponseAt', 'data', 'meta', 'userType', 'testUser', 'testStatus', 'testDiscussionStatus',
                'testEvalStatus', 'testWhatWentWrong', 'testClonedFromUserId', 'testInitialUserId',
                'testDiscussionUserId', 'mergedInto', 'priority', 'takeoverActive', 'takeoverAdminId',
                'takeoverStartedAt', 'takeoverAdminEmail', 'lastAdminMessageAt',
              ]);

              // Realtime delivers full rows regardless of role, so fields hidden from this user's
              // schema (x-visibility) must be dropped before they reach the cache or any broadcast.
              const scrubRealtimeUserUpdate = <T extends Record<string, any>>(
                update: T,
                fieldDefinitions: Record<string, any>
              ): T => {
                const allowed = (key: string) => key in fieldDefinitions || REALTIME_INTERNAL_KEYS.has(key);
                const out: Record<string, any> = {};
                for (const [key, value] of Object.entries(update)) {
                  if (!allowed(key)) continue;
                  out[key] = value;
                }
                if (out.data && typeof out.data === 'object' && !Array.isArray(out.data)) {
                  out.data = Object.fromEntries(Object.entries(out.data).filter(([key]) => allowed(key)));
                }
                return out as T;
              };

              // Sandboxed custom pages cannot subscribe to Supabase; the page bridge relays this.
              const notifyDataChanged = (table: 'users' | 'user_history', event?: string, userId?: number) => {
                window.dispatchEvent(new CustomEvent(DATA_CHANGED_DOM_EVENT, { detail: { table, event, userId } }));
              };

              // Buffer per user and flush at most once per second. Do not throttle the handler
              // itself: each event carries a distinct user's row, so a dropped one is an update the
              // UI never shows (a 5-user burst through throttle() rendered only 2 rows).
              const pendingUserEvents = new Map<number, any>();
              let usersNeedInvalidate = false;
              let usersFlushTimer: ReturnType<typeof setTimeout> | null = null;
              let usersLastFlushAt = 0;
              const USERS_FLUSH_INTERVAL_MS = 1000;

              const flushUserEvents = () => {
                usersLastFlushAt = Date.now();
                const rows = Array.from(pendingUserEvents.values());
                pendingUserEvents.clear();
                const shouldInvalidate = usersNeedInvalidate;
                usersNeedInvalidate = false;

                try {
                  const currentState = useAppState.getState();
                  const patches = new Map<number, Record<string, any>>();
                  const flattenedByUser = new Map<number, Record<string, any>>();
                  let selectedUser = currentState.selectedUser;
                  let discussionUser = currentState.discussionUser;
                  const takeoverUsers = { ...currentState.takeoverUsers };

                  for (const row of rows) {
                    const camelCasePayload = snakeToCamelKeys(row);

                    // Handle column name mismatch: DB column is 'last_response' but app uses 'lastResponseAt'
                    if ('lastResponse' in camelCasePayload && !('lastResponseAt' in camelCasePayload)) {
                      (camelCasePayload).lastResponseAt = camelCasePayload.lastResponse;
                      delete (camelCasePayload).lastResponse;
                    }

                    const timestampFields = ['lastResponseAt', 'createdAt', 'reviewCreatedAt', 'reviewUpdatedAt'];
                    for (const field of timestampFields) {
                      if (field in camelCasePayload && camelCasePayload[field] !== null) {
                        const value = camelCasePayload[field];
                        if (typeof value === 'string') {
                          const utcString = value.endsWith('Z') || value.includes('+') || value.includes('-', 10)
                            ? value
                            : value + 'Z';
                          (camelCasePayload)[field] = new Date(utcString);
                        }
                      }
                    }

                    // Flatten the user data from the realtime payload (matches useUsersQuery format)
                    const flattenedUpdate = scrubRealtimeUserUpdate(
                      flattenUser(camelCasePayload),
                      currentState.fieldDefinitions
                    );
                    flattenedByUser.set(row.id, flattenedUpdate);

                    // Skip selected/discussion merge while updateUser is in flight (prevents stale overwrite)
                    const isPending = pendingUserUpdateIds.has(row.id);
                    if (!isPending && currentState.selectedUserId === row.id) {
                      selectedUser = selectedUser ? { ...selectedUser, ...flattenedUpdate } : flattenedUpdate;
                    }
                    if (!isPending && currentState.discussionUserId === row.id) {
                      discussionUser = discussionUser ? { ...discussionUser, ...flattenedUpdate } : flattenedUpdate;
                    }

                    const userData = row.data;
                    if (userData?.takeoverActive) {
                      takeoverUsers[row.id] = {
                        recipientId: row.recipientId || '',
                        label: userData.nombre || userData.name || row.recipientId || `User ${row.id}`,
                      };
                    } else if (row.id in takeoverUsers) {
                      delete takeoverUsers[row.id];
                    }

                    // Skip fields with in-flight optimistic edits to avoid stale clobber.
                    const pendingForUser = pendingOptimisticEdits.get(row.id);
                    const safeUpdate: Record<string, any> = { ...flattenedUpdate };
                    if (pendingForUser && pendingForUser.size > 0) {
                      for (const k of pendingForUser.keys()) {
                        delete safeUpdate[k];
                      }
                    }
                    patches.set(row.id, safeUpdate);
                  }

                  if (rows.length > 0) {
                    useAppState.setState({ selectedUser, discussionUser, takeoverUsers });

                    // Patch the cache rather than invalidate: invalidation refetches every loaded
                    // infinite-query page, which under bulk AI evals cascades and freezes the UI.
                    // Known gap: patching only rewrites rows already in a page, so an UPDATE that
                    // changes whether a row matches the current view's filters leaves the list wrong
                    // until something else refetches it. See docs/realtime.md "Filter membership".
                    console.log('[Realtime Users] Applying batched UPDATEs', { userIds: Array.from(patches.keys()) });
                    const queryClient = getQueryClient();
                    queryClient.setQueriesData(
                      { queryKey: ['users'] },
                      (oldData: any) => {
                        if (!oldData?.pages) return oldData;
                        return {
                          ...oldData,
                          pages: oldData.pages.map((page: any) => ({
                            ...page,
                            users: page.users.map((u: any) => {
                              const patch = patches.get(u.id);
                              return patch ? { ...u, ...patch } : u;
                            }),
                          })),
                        };
                      }
                    );

                    // Broadcast user updates for iframe pages (avoids full re-fetch)
                    for (const [userId, flattenedUpdate] of flattenedByUser) {
                      window.dispatchEvent(new CustomEvent('apprentice:user-realtime', {
                        detail: { userId, data: flattenedUpdate },
                      }));
                    }
                  }

                  if (shouldInvalidate) {
                    currentState.invalidateUsersCache();
                  }
                } catch (error) {
                  console.warn('Error handling users table change:', error);
                }
              };

              const scheduleUsersFlush = () => {
                if (usersFlushTimer) return;
                const wait = Math.max(0, usersLastFlushAt + USERS_FLUSH_INTERVAL_MS - Date.now());
                usersFlushTimer = setTimeout(() => {
                  usersFlushTimer = null;
                  flushUserEvents();
                }, wait);
              };

              const usersHandler = (payload: any) => {
                console.log('[Realtime Users] Event received', {
                  eventType: payload?.eventType,
                  userId: payload?.new?.id || payload?.old?.id,
                  agentId: payload?.new?.agent_id || payload?.old?.agent_id,
                  currentAgentId: useAppState.getState().currentAgent?.id
                });

                if (!payload?.eventType) {
                  console.warn('[Realtime Users] Invalid payload structure', payload);
                  return;
                }

                // Defensive filter: Skip events from other agents
                // RLS already ensures security, but this improves performance by avoiding unnecessary work
                const currentAgentId = useAppState.getState().currentAgent?.id;
                const eventData = payload.new || payload.old;

                // DELETE payloads carry only the primary key (Postgres default REPLICA IDENTITY), so
                // filtering on a missing agent_id would drop every delete and freeze the list.
                if (eventData && currentAgentId && eventData.agent_id != null) {
                  if (eventData.agent_id !== currentAgentId) {
                    console.log('[Realtime Users] Skipping event from different agent', {
                      eventAgentId: eventData.agent_id,
                      currentAgentId
                    });
                    return;
                  }
                }

                notifyDataChanged('users', undefined, eventData?.id);
                if (payload.new?.user_type === 'eval_run') {
                  getQueryClient().invalidateQueries({ queryKey: EVAL_RUNS_ROOT });
                }

                switch (payload.eventType) {
                  case 'UPDATE':
                    if (payload.new?.id) {
                      pendingUserEvents.set(payload.new.id, payload.new);
                      scheduleUsersFlush();
                    }
                    break;
                  case 'INSERT':
                  case 'DELETE':
                    usersNeedInvalidate = true;
                    scheduleUsersFlush();
                    break;
                }
              };

              // Not throttled: a dropped event is a user whose history never reloads, and
              // throttledReloadHistory already rate-limits the expensive part per user.
              const historyHandler = async (payload: any) => {
                // Get fresh state instead of using stale captured state from onRehydrateStorage
                const currentState = useAppState.getState();
                if (!currentState) return;

                console.log('[Realtime History] Event received', {
                  eventType: payload?.eventType,
                  historyId: payload?.new?.id || payload?.old?.id,
                  userId: payload?.new?.user_id || payload?.old?.user_id,
                  agentId: payload?.new?.agent_id || payload?.old?.agent_id,
                  currentAgentId: currentState.currentAgent?.id
                });

                try {
                  if (!payload?.eventType) {
                    console.warn('[Realtime History] Invalid payload structure', payload);
                    return;
                  }

                  // Defensive filter: Skip events from other agents
                  // RLS already ensures security, but this improves performance by avoiding unnecessary work
                  const currentAgentId = currentState.currentAgent?.id;
                  const eventData = payload.new || payload.old;

                  if (eventData && currentAgentId) {
                    if (eventData.agent_id !== currentAgentId) {
                      console.log('[Realtime History] Skipping event from different agent', {
                        eventAgentId: eventData.agent_id,
                        currentAgentId
                      });
                      return;
                    }
                  }

                  const newUser = payload.new;
                  const oldUser = payload.old;

                  notifyDataChanged('user_history', newUser?.event, newUser?.user_id ?? oldUser?.user_id);

                  switch (payload.eventType) {
                    case 'UPDATE':
                      if (newUser?.user_id) {
                        console.log('[Realtime History] Reloading history for UPDATE', { userId: newUser.user_id });
                        throttledReloadHistory(newUser.user_id, () => currentState.reloadUserHistory(newUser.user_id));
                      } else {
                        console.warn('[Realtime History] Missing user_id in UPDATE payload', payload);
                      }
                      break;
                    case 'INSERT':
                      if (newUser?.user_id) {
                        // Takeover alert: browser notification + audio when taken-over user responds
                        if (newUser.event?.includes('user chat message')) {
                          const takeoverInfo = currentState.takeoverUsers[newUser.user_id];
                          if (takeoverInfo) {
                            const alertUserId = newUser.user_id;
                            showTakeoverResponseAlert(alertUserId, takeoverInfo.label, newUser.message?.substring(0, 100), () => {
                              useAppState.getState().setSelectedUserId(alertUserId);
                            }, currentState.notificationPrefs);
                          }
                        }
                        console.log('[Realtime History] Reloading history for INSERT', { userId: newUser.user_id });
                        throttledReloadHistory(newUser.user_id, () => currentState.reloadUserHistory(newUser.user_id));
                      } else {
                        console.warn('[Realtime History] Missing user_id in INSERT payload', payload);
                      }
                      break;
                    case 'DELETE':
                      // delete payload passes only the id of the history item, not the user id
                      // just reload the selected user history if we have one
                      if (currentState.selectedUserId) {
                        console.log('[Realtime History] Reloading history for DELETE', { userId: currentState.selectedUserId });
                        throttledReloadHistory(currentState.selectedUserId, () => currentState.reloadUserHistory(currentState.selectedUserId!));
                      } else {
                        console.warn('[Realtime History] No user selected, cannot reload history');
                      }
                      break;
                  }
                } catch (error) {
                  console.warn('Error handling user history change:', error);
                }
              };

              // Filter on the server: Realtime RLS-checks every change against every subscriber,
              // and a member of many agents would otherwise receive (and discard) all of them.
              // DELETE payloads carry only the primary key, so deletes stay unfiltered; the handlers
              // already tolerate a missing agent_id on them.
              const onAgentRows = (
                channel: ReturnType<typeof supabase.channel>,
                table: string,
                agentId: number,
                handler: (payload: any) => void
              ) => {
                const filter = `agent_id=eq.${agentId}`;
                return channel
                  .on('postgres_changes', { event: 'INSERT', schema: 'public', table, filter }, handler)
                  .on('postgres_changes', { event: 'UPDATE', schema: 'public', table, filter }, handler)
                  .on('postgres_changes', { event: 'DELETE', schema: 'public', table }, handler);
              };

              // The agent id is part of each topic: supabase.channel() returns an existing channel
              // with the same topic, and subscribe() silently does nothing unless it is fully closed.
              // A view-limited person skips the users and user_history channels: RLS hands them no
              // rows there (they get the view channel below instead), while Realtime still runs the
              // policies for each of their subscriptions on every change.
              const openChannels = (agentId: number, readsAllUsers: boolean) => {
                const userChannels: ReturnType<typeof supabase.channel>[] = [];
                if (readsAllUsers) {
                  const usersChannel = onAgentRows(
                    supabase.channel(`users-changes-${instanceId}-${agentId}`),
                    'users',
                    agentId,
                    usersHandler
                  );

                  console.log('[Realtime] Setting up users channel subscription', { agentId });
                  subscribeWithRecovery(usersChannel, 'users', () => {
                    useAppState.getState().invalidateUsersCache();
                  });

                  const userHistoryChannel = onAgentRows(
                    supabase.channel(`user-history-changes-${instanceId}-${agentId}`),
                    'user_history',
                    agentId,
                    (payload: any) => {
                      historyHandler(payload);
                    }
                  );

                  console.log('[Realtime] Setting up user_history channel subscription', { agentId });
                  subscribeWithRecovery(userHistoryChannel, 'user_history', () => {
                    const s = useAppState.getState();
                    if (s.selectedUserId) s.reloadUserHistory(s.selectedUserId);
                  });
                  userChannels.push(usersChannel, userHistoryChannel);
                }

                const commentsChannel = onAgentRows(
                  supabase.channel(`comments-changes-${instanceId}-${agentId}`),
                  'comments',
                  agentId,
                  async (payload: any) => {
                      try {
                        if (!payload?.eventType) {
                          console.warn('Invalid payload structure in comments-changes event', payload);
                          return;
                        }

                        // Read the store fresh: `state` is the snapshot from rehydration, taken before
                        // the workspace and agent were resolved.
                        const stateAtEvent = useAppState.getState();

                        // `payload.new` is an empty object on a delete, and truthy, so `new || old`
                        // would hand the filter below a row with no columns.
                        const eventData = payload.eventType === 'DELETE' ? payload.old : payload.new;

                        // Defensive filter to avoid reloading a discussion nobody is looking at; RLS
                        // already enforces the boundary. Comments carry no `workspace_id` column, and
                        // an agent belongs to exactly one workspace, so agent_id is the whole check.
                        const currentAgentId = stateAtEvent.currentAgent?.id;

                        if (eventData?.agent_id && currentAgentId) {
                          if (eventData.agent_id !== currentAgentId) {
                            return;
                          }
                        }

                        // Change payloads carry raw column names, hence `linked_user_id`. Under RLS,
                        // Realtime sends only the primary key for a deleted row, so a delete falls back
                        // to the discussion on screen — the only place the removal would be visible.
                        const linkedUserId = payload.new?.linked_user_id
                          ?? payload.old?.linked_user_id
                          ?? (payload.eventType === 'DELETE' ? stateAtEvent.discussionUserId : undefined);
                        if (!linkedUserId) {
                          console.warn('Missing linked_user_id in comments payload', payload);
                          return;
                        }

                        // Inserts, edits and deletes all need the same reload, which refreshes both the
                        // query cache CommentThread renders from and the Zustand copy.
                        await stateAtEvent.reloadUserComments(linkedUserId);

                        const stateAfterReload = useAppState.getState();
                        if (stateAfterReload.discussionUserId === linkedUserId) {
                          useAppState.setState({ discussionUserComments: stateAfterReload.userComments[linkedUserId] || [] });
                        }
                      } catch (error) {
                        console.warn('Error handling comments table change:', error);
                      }
                    }
                  );

                subscribeWithRecovery(commentsChannel, 'comments', () => {
                  const s = useAppState.getState();
                  if (s.discussionUserId) s.reloadUserComments(s.discussionUserId);
                });

                const viewsChannel = onAgentRows(
                  supabase.channel(`views-changes-${instanceId}-${agentId}`),
                  'views',
                  agentId,
                  async (payload: any) => {
                      const currentState = useAppState.getState();
                      if (!currentState) return;

                      try {
                        if (!payload?.eventType) {
                          console.warn('Invalid payload structure in views-changes event', payload);
                          return;
                        }

                        // Defensive filter: Skip events from other agents
                        const currentAgentId = currentState.currentAgent?.id;
                        const eventData = payload.new || payload.old;

                        if (eventData && currentAgentId) {
                          if (eventData.agent_id !== currentAgentId) {
                            return;
                          }
                        }

                        currentState.invalidateViewsCache();
                        await currentState.reloadViews();
                        console.log('Views updated via realtime subscription');
                      } catch (error) {
                        console.warn('Error handling views table change:', error);
                      }
                    }
                  );

                subscribeWithRecovery(viewsChannel, 'views', () => {
                  const s = useAppState.getState();
                  s.invalidateViewsCache();
                  s.reloadViews();
                });

                // Subscribe to config_table changes for field definitions
                const configTableChannel = onAgentRows(
                  supabase.channel(`config-table-changes-${instanceId}-${agentId}`),
                  'config_table',
                  agentId,
                  async (payload: any) => {
                      const currentState = useAppState.getState();
                      if (!currentState) return;

                      try {
                        if (!payload?.eventType) {
                          console.warn('Invalid payload structure in config-table-changes event', payload);
                          return;
                        }

                        // Defensive filter: Skip events from other agents
                        const currentAgentId = currentState.currentAgent?.id;
                        const eventData = payload.new || payload.old;

                        if (eventData && currentAgentId) {
                          if (eventData.agent_id !== currentAgentId) {
                            return;
                          }
                        }

                        await currentState.reloadFieldDefinitions();
                        console.log('Field definitions updated via realtime subscription');
                      } catch (error) {
                        console.warn('Error handling config_table change:', error);
                      }
                    }
                  );

                subscribeWithRecovery(configTableChannel, 'config_table', () => {
                  useAppState.getState().reloadFieldDefinitions();
                });

                // The version picker re-reads its rows from the API; the payload only says something changed.
                const agentVersionsChannel = onAgentRows(
                  supabase.channel(`agent-versions-changes-${instanceId}-${agentId}`),
                  'agent_versions',
                  agentId,
                  (payload: any) => {
                    const rowAgentId = (payload?.new ?? payload?.old)?.agent_id;
                    if (rowAgentId && rowAgentId !== useAppState.getState().currentAgent?.id) return;
                    useVersionTree.getState().syncFromRealtime();
                  }
                );

                subscribeWithRecovery(agentVersionsChannel, 'agent_versions', () => {
                  useVersionTree.getState().syncFromRealtime();
                });

                return [
                  ...userChannels,
                  commentsChannel,
                  viewsChannel,
                  configTableChannel,
                  agentVersionsChannel,
                ];
              };

              // The agent is resolved after rehydration, and the filters are baked into each
              // binding, so channels open once it is known and reopen whenever it changes.
              let openedChannels: ReturnType<typeof supabase.channel>[] = [];
              let openedKey: string | null = null;

              const closeChannels = () => {
                for (const channel of openedChannels) {
                  retiredChannels.add(channel);
                  try {
                    supabase.removeChannel(channel);
                  } catch (error) {
                    console.warn('Error cleaning up Supabase channels:', error);
                  }
                }
                openedChannels = [];
              };

              const syncChannelsToAgent = () => {
                const { currentAgent, currentAgentAccess: access } = useAppState.getState();
                const agentId = currentAgent?.id ?? null;
                // Access arrives after the agent; until then assume full user access, as before.
                const readsAllUsers = !access || canOnAllViews(access, 'users:read');
                const key = agentId == null ? null : `${agentId}:${readsAllUsers}`;
                if (key === openedKey) return;
                closeChannels();
                openedKey = key;
                if (agentId == null) return;
                openedChannels = openChannels(agentId, readsAllUsers);
              };

              syncChannelsToAgent();
              useAppState.subscribe((current, previous) => {
                if (
                  current.currentAgent?.id !== previous.currentAgent?.id ||
                  current.currentAgentAccess !== previous.currentAgentAccess
                )
                  syncChannelsToAgent();
              });

              // View-limited people get no user rows over postgres_changes (RLS); they join the open
              // view's private broadcast channel instead, after the Next.js app grants it. docs/realtime.md, "View channels".
              let viewChannel: ReturnType<typeof supabase.channel> | null = null;
              let viewChannelKey: string | null = null;

              const closeViewChannel = () => {
                if (!viewChannel) return;
                retiredChannels.add(viewChannel);
                try {
                  supabase.removeChannel(viewChannel);
                } catch (error) {
                  console.warn('Error cleaning up view channel:', error);
                }
                viewChannel = null;
              };

              const syncViewChannel = async (isRetry = false) => {
                const { currentAgentAccess: access, currentAgent, currentWorkspace, currentViewId } = useAppState.getState();
                const limited = !!access && !canOnAllViews(access, 'users:read');
                const key =
                  limited && currentAgent && currentWorkspace && currentViewId && /^\d+$/.test(String(currentViewId))
                    ? `${currentAgent.id}:${currentViewId}`
                    : null;
                if (key === viewChannelKey && !isRetry) return;
                closeViewChannel();
                viewChannelKey = key;
                if (!key || !currentAgent || !currentWorkspace) return;
                try {
                  const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/views/${currentViewId}/live`, { method: 'POST' });
                  if (!response.ok || viewChannelKey !== key) return;
                  const { topic } = await response.json();
                  const channel = supabase
                    .channel(topic, { config: { private: true } })
                    .on('broadcast', { event: 'contact' }, ({ payload }) => usersHandler(payload))
                    .on('broadcast', { event: 'history' }, ({ payload }) => historyHandler(payload));
                  viewChannel = channel;
                  let settled = false;
                  channel.subscribe((status: string) => {
                    if (settled || retiredChannels.has(channel)) return;
                    if (status === 'SUBSCRIBED') {
                      if (isRetry) useAppState.getState().invalidateUsersCache();
                      return;
                    }
                    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                      settled = true;
                      console.warn('[Realtime] view channel failed, rejoining in 5s', { topic, status });
                      // Rejoin through the grant route: the grant may have expired or been revoked.
                      setTimeout(() => {
                        if (viewChannelKey === key && !retiredChannels.has(channel)) void syncViewChannel(true);
                      }, 5000);
                    }
                  });
                } catch (error) {
                  console.warn('[Realtime] view channel setup failed', error);
                }
              };

              void syncViewChannel();
              useAppState.subscribe((current, previous) => {
                if (
                  current.currentViewId !== previous.currentViewId ||
                  current.currentAgentAccess !== previous.currentAgentAccess ||
                  current.currentAgent?.id !== previous.currentAgent?.id
                ) {
                  void syncViewChannel();
                }
              });
              window.addEventListener('beforeunload', closeViewChannel);

              window.addEventListener('beforeunload', closeChannels);

              // Visibility-based reconnection: when user returns to tab after being away,
              // check connection health and refresh data to catch any missed updates
              let wasHidden = false;

              const handleVisibilityChange = () => {
                if (document.hidden) {
                  wasHidden = true;
                  console.log('[Realtime] Tab hidden, marking for reconnection check');
                } else if (wasHidden) {
                  wasHidden = false;
                  console.log('[Realtime] Tab visible again, checking connection health');

                  const realtimeState = supabase.realtime.connectionState();
                  console.log('[Realtime] Connection state:', realtimeState);

                  if (realtimeState !== 'open') {
                    console.log('[Realtime] Connection not open, reconnecting...');
                    supabase.realtime.connect();
                  }

                  const currentState = useAppState.getState();
                  if (currentState.selectedUserId) {
                    currentState.reloadUserHistory(currentState.selectedUserId);
                  }
                  if (currentState.discussionUserId) {
                    currentState.reloadUserComments(currentState.discussionUserId);
                  }
                }
              };

              document.addEventListener('visibilitychange', handleVisibilityChange);

              // Also check connection health periodically (every 60s) as a fallback
              const healthCheckInterval = setInterval(() => {
                const realtimeState = supabase.realtime.connectionState();
                if (realtimeState !== 'open') {
                  console.warn('[Realtime] Connection degraded:', realtimeState);
                  supabase.realtime.connect();
                }
              }, 60000);

              window.addEventListener('beforeunload', () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                clearInterval(healthCheckInterval);
              });

              console.log('[Realtime] Subscriptions follow the current agent', {
                channels: [
                  'users',
                  'user_history',
                  'comments',
                  'views',
                  'config_table'
                ]
              });

            } catch (error) {
              console.error('[Realtime] Failed to set up Supabase realtime subscriptions:', error);
            }
          }
        }
      )
    )
  )
);

// Track changes to view-defining properties
useAppState.subscribe((state) => {
  if (!state.currentViewId) {
    if (state.viewHasUnsavedChanges) {
      useAppState.setState({ viewHasUnsavedChanges: false });
    }
    return;
  }

  if (!viewStateSnapshot || viewStateSnapshot.currentViewId !== state.currentViewId) {
    return;
  }

  let hasChanges = false;
  const changes: string[] = [];

  const snapshotFilters = JSON.stringify(viewStateSnapshot.filters || []);
  const stateFilters = JSON.stringify(state.filters || []);
  if (snapshotFilters !== stateFilters) {
    hasChanges = true;
    changes.push('filters');
  }

  const snapshotColumns = JSON.stringify(viewStateSnapshot.visibleColumns || []);
  const stateColumns = JSON.stringify(state.visibleColumns || []);
  if (snapshotColumns !== stateColumns) {
    hasChanges = true;
    changes.push('visibleColumns');
  }

  const snapshotColumnOrder = JSON.stringify(viewStateSnapshot.columnOrder || []);
  const stateColumnOrder = JSON.stringify(state.columnOrder || []);
  if (snapshotColumnOrder !== stateColumnOrder) {
    hasChanges = true;
    changes.push('columnOrder');
    log.info('view change detection: columnOrder changed', {
      snapshotColumnOrder: viewStateSnapshot.columnOrder,
      stateColumnOrder: state.columnOrder
    });
  }

  if (viewStateSnapshot.sortBy !== state.sortBy) {
    hasChanges = true;
    changes.push('sortBy');
  }

  if (viewStateSnapshot.sortOrder !== state.sortOrder) {
    hasChanges = true;
    changes.push('sortOrder');
    log.info('view change detection: sortOrder changed', {
      snapshotSortOrder: viewStateSnapshot.sortOrder,
      stateSortOrder: state.sortOrder
    });
  }

  if (viewStateSnapshot.logicalOperator !== state.logicalOperator) {
    hasChanges = true;
    changes.push('logicalOperator');
    log.info('view change detection: logicalOperator changed', {
      snapshotLogicalOperator: viewStateSnapshot.logicalOperator,
      stateLogicalOperator: state.logicalOperator
    });
  }

  // Only update if the flag has actually changed to avoid infinite loops
  if (hasChanges !== state.viewHasUnsavedChanges) {
    console.log('view change detection: hasChanges changed', {
      hasChanges,
      stateHasUnsavedChanges: state.viewHasUnsavedChanges
    });
    useAppState.setState({ viewHasUnsavedChanges: hasChanges });
  }
});
